import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { API_BASE_URL } from '../config/env';
import { useAuth } from '../context/AuthContext';
import { EMISOR_TRATAMIENTO_ADMIN } from '../utils/tratamientoAprobacion';
import productos from '../data/productos.json';
import { buildCrmSwalBody, fireConfirmDialog } from '../utils/swalConfirm';
import { CrmStatusBadgeFrom } from '../components/CrmStatusBadge';
import TratamientoPlanResumen from '../components/TratamientoPlanResumen';
import AdminEmisorPin from '../components/AdminEmisorPin';
import RecetaEmisionVentanaPin from '../components/RecetaEmisionVentanaPin';
import PacienteExternoBadge from '../components/PacienteExternoBadge';
import PamiBadge from '../components/PamiBadge';
import PedidoIngresoOrigenBadge from '../components/PedidoIngresoOrigenBadge';
import { addDaysIso, formatFechaCorta, resolvePlanDosificacionPedido } from '../utils/tratamientoDosificacion';
import { resolveRecetaStatusBadge } from '../utils/crmStatusBadge';
import '../styles/conversaciones-premium.css';
import '../styles/gestion-derivacion-premium.css';
import '../styles/recetas-emitir-premium.css';
import { CwListSearch } from '../components/conversaciones/CwUiParts';
import { fetchConversationTemplates, sendConversationTemplate } from '../api/chatwoot';

const PRODUCTOS_MAP = new Map((Array.isArray(productos) ? productos : []).map((p) => [Number(p.id), p.nombre]));

function isMedicationIdPlaceholder(value) {
  return /^id\s*:\s*\d+$/i.test(String(value || '').trim());
}

function firstNonEmpty(...values) {
  for (const value of values) {
    const text = String(value || '').trim();
    if (text && !['null', 'undefined'].includes(text.toLowerCase())) return text;
  }
  return '';
}

function capitalizeFirstLetter(value) {
  const text = String(value || '').trim();
  if (!text || ['null', 'undefined'].includes(text.toLowerCase())) return '';
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function isValidHttpUrl(value) {
  const text = String(value || '').trim();
  if (!text) return false;
  try {
    const parsed = new URL(text);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch (_) {
    return false;
  }
}

function getUrlPreviewMeta(value) {
  const text = String(value || '').trim();
  if (!text) return null;
  try {
    const parsed = new URL(text);
    return {
      hostname: parsed.hostname.replace(/^www\./i, ''),
      pathname: parsed.pathname && parsed.pathname !== '/' ? parsed.pathname : '',
      href: parsed.toString()
    };
  } catch (_) {
    return null;
  }
}

function isLikelyIframeBlockedHost(hostname) {
  const host = String(hostname || '').trim().toLowerCase();
  if (!host) return false;
  return [
    'google.com',
    'youtube.com',
    'youtu.be',
    'facebook.com',
    'instagram.com',
    'whatsapp.com',
    'web.whatsapp.com',
    'linkedin.com',
    'x.com',
    'twitter.com'
  ].some((blockedHost) => host === blockedHost || host.endsWith(`.${blockedHost}`));
}

function normalizeMedicationFields(source) {
  const cronicaRaw = String(source?.cronica ?? '').trim().toLowerCase();
  const esCronico = source?.es_cronico === true
    || source?.es_cronico === 1
    || ['1', 's', 'si', 'sí', 'true', 'yes'].includes(String(source?.es_cronico ?? '').trim().toLowerCase())
    || ['1', 's', 'si', 'sí', 'true', 'yes', 'cronico', 'crónico'].includes(cronicaRaw);

  return {
    medicamento_nombre: firstNonEmpty(
      source?.producto,
      source?.nombre_comercial,
      source?.medicamento_nombre,
      source?.medicamento
    ),
    nombre_generico: firstNonEmpty(source?.nombre_generico, source?.medicamento_generico, source?.droganombre),
    nombre_comercial: firstNonEmpty(source?.producto, source?.nombre_comercial),
    laboratorio: firstNonEmpty(source?.laboratorio, source?.medicamento_laboratorio, source?.desclaboratorio, source?.nombrelab),
    presentacion: firstNonEmpty(source?.presentacion, source?.medicamento_presentacion),
    concentracion: firstNonEmpty(source?.concentracion, source?.medicamento_concentracion),
    accion_nombre: firstNonEmpty(source?.accion_nombre, source?.accionnombre),
    categoria_nombre: firstNonEmpty(source?.categoria_nombre, source?.categorianombre, source?.categoria, source?.sub_categoria, source?.subrubronombre),
    condicion_venta: firstNonEmpty(source?.condicion_venta, source?.tipoventadesc),
    refrigeracion: firstNonEmpty(source?.refrigeracion),
    es_cronico: esCronico,
    cronica: firstNonEmpty(source?.cronica, esCronico ? 'si' : '')
  };
}

function mergeMedicationFields(base, extra) {
  const left = normalizeMedicationFields(base || {});
  const right = normalizeMedicationFields(extra || {});
  return {
    medicamento_nombre: firstNonEmpty(right.medicamento_nombre, left.medicamento_nombre),
    nombre_generico: firstNonEmpty(right.nombre_generico, left.nombre_generico),
    nombre_comercial: firstNonEmpty(right.nombre_comercial, left.nombre_comercial),
    laboratorio: firstNonEmpty(right.laboratorio, left.laboratorio),
    presentacion: firstNonEmpty(right.presentacion, left.presentacion),
    concentracion: firstNonEmpty(right.concentracion, left.concentracion),
    accion_nombre: firstNonEmpty(right.accion_nombre, left.accion_nombre),
    categoria_nombre: firstNonEmpty(right.categoria_nombre, left.categoria_nombre),
    condicion_venta: firstNonEmpty(right.condicion_venta, left.condicion_venta),
    refrigeracion: firstNonEmpty(right.refrigeracion, left.refrigeracion),
    es_cronico: Boolean(right.es_cronico || left.es_cronico),
    cronica: firstNonEmpty(right.cronica, left.cronica)
  };
}

function formatFechaHoraCorta(value) {
  if (!value) return '';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (_) {
    return '';
  }
}

const RECETA_EMITIDA_ESTADOS = new Set(['emitida_sistema_externo', 'confirmada_dispensa']);

function isRecetaEstadoEmitida(estado) {
  return RECETA_EMITIDA_ESTADOS.has(String(estado || '').trim().toLowerCase());
}

function recetaEstadoMergeRank(estado) {
  if (isRecetaEstadoEmitida(estado)) return 0;
  const s = String(estado || '').trim().toLowerCase();
  if (s === 'pendiente') return 2;
  return 1;
}

function pickPersistedRecetaMatch(persisted, numero) {
  const matches = (persisted || []).filter((pr) => Number(pr.numero_receta) === Number(numero));
  if (!matches.length) return null;
  return matches.slice().sort((a, b) => {
    const rankDiff = recetaEstadoMergeRank(a.estado) - recetaEstadoMergeRank(b.estado);
    if (rankDiff !== 0) return rankDiff;
    return Number(b.id || 0) - Number(a.id || 0);
  })[0];
}

function mergeRecetaFromPersisted(rec, match) {
  if (!match) return rec;
  const localEmitida = isRecetaEstadoEmitida(rec.estado);
  const persistedEmitida = isRecetaEstadoEmitida(match.estado);
  const nextEstado = localEmitida
    ? rec.estado
    : (persistedEmitida ? match.estado : (match.estado || rec.estado || 'pendiente'));
  const emitida = isRecetaEstadoEmitida(nextEstado);

  return {
    ...rec,
    estado: nextEstado,
    nro_externo: match.nro_receta_externo || rec.nro_externo || '',
    fecha_emitida: match.fecha_emitida ? new Date(match.fecha_emitida).toLocaleDateString('es-AR') : rec.fecha_emitida,
    receta_url: match.receta_url || rec.receta_url || '',
    pedido_farmacia_id: match.pedido_farmacia_id || null,
    recordatorio_vencimiento_enviado_at: emitida ? null : (match.recordatorio_vencimiento_enviado_at || rec.recordatorio_vencimiento_enviado_at || null),
    respuesta_renovacion: emitida ? null : (match.respuesta_renovacion || rec.respuesta_renovacion || null),
    respuesta_renovacion_at: emitida ? null : (match.respuesta_renovacion_at || rec.respuesta_renovacion_at || null),
    respuesta_renovacion_mensaje: emitida ? null : (match.respuesta_renovacion_mensaje || rec.respuesta_renovacion_mensaje || null)
  };
}

function resolveRenovacionPacienteMeta(receta) {
  if (!receta || String(receta?.estado || '').trim().toLowerCase() !== 'pendiente') return null;
  const respuesta = String(receta?.respuesta_renovacion || '').trim().toLowerCase();
  const recordatorioEnviado = Boolean(receta?.recordatorio_vencimiento_enviado_at);
  const respuestaAt = receta?.respuesta_renovacion_at || null;
  const esperando = recordatorioEnviado && !respuesta;
  if (respuesta === 'si') {
    return {
      tone: 'yes',
      title: 'Paciente quiere renovar',
      subtitle: `Confirmó por WhatsApp${respuestaAt ? ` · ${formatFechaHoraCorta(respuestaAt)}` : ''}`,
      hint: 'El paciente respondió afirmativamente al recordatorio. Podés avanzar con la emisión cuando corresponda.',
      icon: '✅'
    };
  }
  if (respuesta === 'no') {
    return {
      tone: 'no',
      title: 'Paciente no renovará por ahora',
      subtitle: `Respondió por WhatsApp${respuestaAt ? ` · ${formatFechaHoraCorta(respuestaAt)}` : ''}`,
      hint: 'Registramos que por ahora no desea renovar. El recordatorio quedó cerrado.',
      icon: '🙏'
    };
  }
  if (esperando) {
    return {
      tone: 'waiting',
      title: 'Esperando respuesta del paciente',
      subtitle: `Recordatorio enviado${receta?.recordatorio_vencimiento_enviado_at ? ` · ${formatFechaHoraCorta(receta.recordatorio_vencimiento_enviado_at)}` : ''}`,
      hint: 'El paciente recibió el aviso por WhatsApp. La pantalla se actualiza sola cuando responda.',
      icon: '💬'
    };
  }
  return null;
}

function RenovacionPacienteRespuestaCard({ receta }) {
  const meta = resolveRenovacionPacienteMeta(receta);
  if (!meta) return null;
  return (
    <div className={`crm-re-renov-card crm-re-renov-card--${meta.tone}`} title={meta.hint}>
      <div className="crm-re-renov-card__glow" aria-hidden="true" />
      <div className="crm-re-renov-card__icon" aria-hidden="true">{meta.icon}</div>
      <div className="crm-re-renov-card__copy">
        <div className="crm-re-renov-card__title">{meta.title}</div>
        <div className="crm-re-renov-card__subtitle">{meta.subtitle}</div>
      </div>
      {meta.tone === 'waiting' ? (
        <span className="crm-re-renov-card__pulse" aria-label="Esperando respuesta" />
      ) : null}
    </div>
  );
}

function buildMedicationDisplay(source) {
  const details = normalizeMedicationFields(source);
  const isCronicoCategory = ['cronico', 'crónico'].includes(String(details.categoria_nombre || '').trim().toLowerCase());
  const comercial = firstNonEmpty(
    source?.nombre_comercial,
    source?.producto,
    details.nombre_comercial,
    details.medicamento_nombre,
    source?.medicamento
  );
  const generico = capitalizeFirstLetter(details.nombre_generico);
  const presentacion = details.presentacion;
  const concentracionRaw = details.concentracion;
  const laboratorioRaw = details.laboratorio;
  const laboratorio = laboratorioRaw && laboratorioRaw !== '0' ? laboratorioRaw : '';
  const fallback = firstNonEmpty(details.medicamento_nombre, source?.medicamento);
  const title = comercial || fallback || generico || 'Medicamento no informado';
  const titleKey = title.toLowerCase();
  const concentrationKey = concentracionRaw.toLowerCase();
  const titleContainsConcentration = concentrationKey && titleKey.includes(concentrationKey);
  const presentationContainsConcentration = concentrationKey && presentacion.toLowerCase().includes(concentrationKey);
  const concentracion = /[a-z]/i.test(concentracionRaw) && !titleContainsConcentration && !presentationContainsConcentration
    ? concentracionRaw
    : '';
  const genericLine = generico && generico.toLowerCase() !== titleKey
    ? `Droga: ${generico}`
    : '';
  const accionNombre = capitalizeFirstLetter(details.accion_nombre);
  const categoriaNombre = isCronicoCategory ? '' : capitalizeFirstLetter(details.categoria_nombre);
  const meta = [
    concentracion,
    presentacion,
    accionNombre,
    categoriaNombre,
    details.es_cronico ? 'Crónico' : '',
    String(details.refrigeracion || '').trim().toUpperCase() === 'S' ? 'Refrigerado' : ''
  ].filter(Boolean);

  return {
    title,
    laboratoryLabel: laboratorio ? `Lab. ${laboratorio}` : '',
    genericLine,
    meta
  };
}

function resolvePedidoAdministrativoNombre(pedido) {
  return String(pedido?.administrativo || pedido?.administrativo_nombre || '').trim();
}

function isPedidoEmisionAdministrativo(pedido) {
  const emisor = String(pedido?.emisor_tratamiento || 'medico').trim().toLowerCase();
  return emisor === EMISOR_TRATAMIENTO_ADMIN;
}

function renderRecetaEmisionStatus({
  emittedCount = 0,
  totalPendingCount = 0,
  hiddenPendientesInGroup = 0,
  layout = 'chips'
}) {
  if (layout === 'cluster') {
    if (emittedCount === 0 && totalPendingCount === 0 && hiddenPendientesInGroup === 0) {
      return (
        <div className="crm-re-status-cluster">
          <div className="crm-re-status-pill crm-re-status-pill--muted">
            <span className="crm-re-status-pill__label">Sin recetas</span>
          </div>
        </div>
      );
    }

    return (
      <div className="crm-re-status-cluster">
        {emittedCount > 0 ? (
          <div className="crm-re-status-pill crm-re-status-pill--ok">
            <span className="crm-re-status-pill__value">{emittedCount}</span>
            <span className="crm-re-status-pill__label">emitida{emittedCount > 1 ? 's' : ''}</span>
          </div>
        ) : null}
        {totalPendingCount > 0 ? (
          <div className="crm-re-status-pill crm-re-status-pill--pending">
            <span className="crm-re-status-pill__value">{totalPendingCount}</span>
            <span className="crm-re-status-pill__label">pendiente{totalPendingCount > 1 ? 's' : ''}</span>
          </div>
        ) : null}
        {hiddenPendientesInGroup > 0 ? (
          <div className="crm-re-status-pill crm-re-status-pill--hidden">
            <span className="crm-re-status-pill__value">{hiddenPendientesInGroup}</span>
            <span className="crm-re-status-pill__label">oculta{hiddenPendientesInGroup > 1 ? 's' : ''}</span>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="crm-re-actors-strip__chips">
      {emittedCount > 0 ? <span className="crm-re-count-chip crm-re-count-chip--ok">{`${emittedCount} emitida${emittedCount > 1 ? 's' : ''}`}</span> : null}
      {totalPendingCount > 0 ? <span className="crm-re-count-chip crm-re-count-chip--pending">{`${totalPendingCount} pendiente${totalPendingCount > 1 ? 's' : ''}`}</span> : null}
      {hiddenPendientesInGroup > 0 ? <span className="crm-re-count-chip crm-re-count-chip--muted">{`Ocultas: ${hiddenPendientesInGroup}`}</span> : null}
      {emittedCount === 0 && totalPendingCount === 0 ? <span className="crm-re-count-chip crm-re-count-chip--muted">Sin recetas</span> : null}
    </div>
  );
}

function resolveMedicationName(source) {
  const title = firstNonEmpty(
    source?.producto,
    source?.nombre_comercial,
    source?.medicamento_nombre,
    source?.medicamento,
    PRODUCTOS_MAP.get(Number(source?.medicamento_id))
  );
  if (title && !isMedicationIdPlaceholder(title)) return title;

  const generic = firstNonEmpty(source?.nombre_generico, source?.medicamento_generico, source?.droganombre);
  const concentration = firstNonEmpty(source?.concentracion, source?.medicamento_concentracion);
  if (generic) {
    const genericKey = generic.toLowerCase();
    const concentrationKey = concentration.toLowerCase();
    const withConcentration = concentration && !genericKey.includes(concentrationKey)
      ? `${generic} ${concentration}`
      : generic;
    return withConcentration;
  }

  return source?.medicamento_id ? `ID:${source.medicamento_id}` : '';
}

export default function RecetasEmitir() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const RECETAS_EMITIR_CACHE_KEY = `recetasEmitirCacheV2:${String(user?.id || user?.username || 'anon')}`;
  const RECETAS_EMITIR_CACHE_TTL_MS = 2 * 60 * 1000;
  const EMISION_WINDOW_PASADO_DIAS = 5;
  const EMISION_WINDOW_FUTURO_DIAS = 5;
  const [data, setData] = useState({ pedidos: [], recetas: [] });
  const [initialLoading, setInitialLoading] = useState(true);
  const [modalEmitirOpen, setModalEmitirOpen] = useState(false);
  const [selectedRecetaId, setSelectedRecetaId] = useState(null);
  const [emitirNro, setEmitirNro] = useState('');
  const [emitirUrl, setEmitirUrl] = useState('');
  const [confirmandoEmision, setConfirmandoEmision] = useState(false);
  const [sendingMensajeRecetaId, setSendingMensajeRecetaId] = useState(null);
  const [emitStep, setEmitStep] = useState('whatsapp');
  const [emitWa, setEmitWa] = useState(false);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [templateConversationId, setTemplateConversationId] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateParamValues, setTemplateParamValues] = useState([]);
  const [sendingTemplate, setSendingTemplate] = useState(false);
  const [openMedicationDetails, setOpenMedicationDetails] = useState('');
  const [viewport, setViewport] = useState(() => ({
    width: typeof window !== 'undefined' ? window.innerWidth : 1366,
    height: typeof window !== 'undefined' ? window.innerHeight : 768
  }));

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onResize = () => setViewport({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!openMedicationDetails) return undefined;

    const handlePointerDownOutside = (event) => {
      const root = event.target?.closest?.('[data-medication-detail-root="true"]');
      if (root) return;
      setOpenMedicationDetails('');
    };

    document.addEventListener('mousedown', handlePointerDownOutside);
    document.addEventListener('touchstart', handlePointerDownOutside);

    return () => {
      document.removeEventListener('mousedown', handlePointerDownOutside);
      document.removeEventListener('touchstart', handlePointerDownOutside);
    };
  }, [openMedicationDetails]);

  const isMobile = viewport.width <= 768;
  const isTablet = viewport.width > 768 && viewport.width <= 1100;
  const isSmallLandscape = viewport.width <= 1024 && viewport.width > viewport.height;
  const emitirUrlValid = useMemo(() => isValidHttpUrl(emitirUrl), [emitirUrl]);
  const emitirUrlPreview = useMemo(() => getUrlPreviewMeta(emitirUrl), [emitirUrl]);
  const emitirUrlIframeBlocked = useMemo(
    () => isLikelyIframeBlockedHost(emitirUrlPreview?.hostname),
    [emitirUrlPreview]
  );
  const canSubmitEmision = useMemo(
    () => !confirmandoEmision && Boolean(emitirNro.trim()) && emitirUrlValid,
    [confirmandoEmision, emitirNro, emitirUrlValid]
  );

  const selectedEmisionContext = useMemo(() => {
    if (!selectedRecetaId) return null;
    const receta = (data.recetas || []).find((r) => r.id === selectedRecetaId);
    if (!receta) return null;
    const pedido = (data.pedidos || []).find((p) => Number(p.id) === Number(receta.pedido_id));
    const recetasGrupo = (data.recetas || []).filter((r) => Number(r.pedido_id) === Number(receta.pedido_id));
    return { receta, pedido, recetasGrupo };
  }, [selectedRecetaId, data.recetas, data.pedidos]);

  const emitidas = data.recetas.filter(r => r.estado === 'emitida_sistema_externo' || r.estado === 'confirmada_dispensa');

  // Search + pagination for grouped pedidos
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [expandedHiddenPedidos, setExpandedHiddenPedidos] = useState(() => new Set());
  const [modalHistorialGlobalOpen, setModalHistorialGlobalOpen] = useState(false);
  const [historialGlobalDni, setHistorialGlobalDni] = useState('');
  const [historialGlobalSearch, setHistorialGlobalSearch] = useState('');
  const [historialGlobalPacienteSearch, setHistorialGlobalPacienteSearch] = useState('');
  const [historialGlobalDesde, setHistorialGlobalDesde] = useState('');
  const [historialGlobalHasta, setHistorialGlobalHasta] = useState('');
  const [historialGlobalEstado, setHistorialGlobalEstado] = useState('todos');
  const [historialGlobalOrder, setHistorialGlobalOrder] = useState('fecha_desc');
  const [historialGlobalPage, setHistorialGlobalPage] = useState(1);
  const [historialGlobalItemsPerPage, setHistorialGlobalItemsPerPage] = useState(6);
  const [pageInput, setPageInput] = useState(String(page));
  useEffect(() => { setPageInput(String(page || 1)); }, [page, /*totalPages will be declared later*/ itemsPerPage]);

  const handlePageInputChange = (e) => {
    const raw = e.target.value;
    const numeric = raw.replace(/\D/g, '');
    setPageInput(numeric);
  };

  const goToInputPage = () => {
    if (!pageInput) return;
    let next = Number(pageInput);
    if (Number.isNaN(next)) return;
    if (next < 1) next = 1;
    if (next > totalPages) next = totalPages;
    if (next === page) return;
    setPage(next);
  };

  // Load pedidos_receta fast first, then hydrate persisted recetas progressively
  useEffect(() => {
    let mounted = true;
    const ac = new AbortController();
    const writeCache = (snapshot) => {
      try {
        sessionStorage.setItem(RECETAS_EMITIR_CACHE_KEY, JSON.stringify({ at: Date.now(), data: snapshot }));
      } catch (_) { /* ignore */ }
    };

    // Instant paint from short-lived cache to reduce perceived latency.
    try {
      const rawCache = sessionStorage.getItem(RECETAS_EMITIR_CACHE_KEY);
      if (rawCache) {
        const parsed = JSON.parse(rawCache);
        const ageMs = Date.now() - Number(parsed?.at || 0);
        if (parsed?.data && ageMs >= 0 && ageMs <= RECETAS_EMITIR_CACHE_TTL_MS) {
          setData(parsed.data);
        }
      }
    } catch (_) { /* ignore cache read errors */ }

    const buildRecetasFromPedidos = (pedidosRaw) => {
      const recetas = [];
      const pedidos = (pedidosRaw || []).map((p) => ({
        ...p,
        ...mergeMedicationFields({}, p),
        medicamento: resolveMedicationName(p),
        _dni_display: p.dni_normalizado || p.paciente_dni || p.solicitante_dni || ''
      }));

      for (const p of pedidos) {
        if (!p || !p.id) continue;
        if (!['aprobado', 'en_tratamiento'].includes(String(p.estado))) continue;
        const cantidad = Number(p.cantidad_recetas) || 1;
        const fechaInicioRaw = p.fecha_inicio || p.fecha_solicitud || null;
        const plan = resolvePlanDosificacionPedido(p);
        for (let i = 1; i <= cantidad; i++) {
          const slot = plan.slots[i - 1];
          let fechaPrev = slot?.fechaPrevista || '';
          if (!fechaPrev && fechaInicioRaw) {
            const offset = (i - 1) * (plan.intervaloDias || 30);
            fechaPrev = addDaysIso(fechaInicioRaw, offset) || '';
          }
          recetas.push({
            id: `${p.id}-${i}`,
            pedido_id: p.id,
            numero: i,
            fecha_prevista: fechaPrev,
            fecha_inicio_cobertura: slot?.fechaInicio || fechaPrev || null,
            fecha_fin_cobertura: slot?.fechaFin || null,
            duracion_dias: slot?.duracionDias || plan.intervaloDias || null,
            unidades_receta: slot?.unidades || plan.unidadesPorReceta || null,
            fecha_emitida: null,
            estado: 'pendiente',
            nro_externo: '',
            paciente: p.paciente_nombre || p.paciente || p.paciente_dni || p.solicitante_dni || '',
            medicamento: p.medicamento,
            ...mergeMedicationFields({}, p),
            medicamento_id: p.medicamento_id
          });
        }
      }

      return { pedidos, recetas };
    };

    const applyPersistedRecetas = (baseRecetas, persistedByPedido) => (
      baseRecetas.map((rec) => {
        const persisted = persistedByPedido[String(rec.pedido_id)] || [];
        const match = pickPersistedRecetaMatch(persisted, rec.numero);
        return mergeRecetaFromPersisted(rec, match);
      })
    );

    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/asociacion-recetas/pedidos`, { credentials: 'include', signal: ac.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        const pedidosRaw = Array.isArray(json?.pedidos) ? json.pedidos : (Array.isArray(json) ? json : []);
        const { pedidos, recetas } = buildRecetasFromPedidos(pedidosRaw);

        // Paint immediately with base data so the screen is usable quickly.
        if (mounted) {
          const base = { pedidos, recetas };
          setData(base);
          writeCache(base);
        }

        // Enriquecer medicamentos faltantes por medicamento_id desde vademecum
        // para evitar mostrar "ID:xxx" cuando el pedido no trae nombre.
        const unknownIds = Array.from(
          new Set(
            (pedidosRaw || [])
              .map((p) => Number(p?.medicamento_id))
              .filter((mid) => Number.isFinite(mid) && mid > 0)
              .filter((mid) => {
                const p = (pedidosRaw || []).find((x) => Number(x?.medicamento_id) === mid);
                const resolvedName = resolveMedicationName(p);
                const hasName = Boolean(resolvedName) && !isMedicationIdPlaceholder(resolvedName);
                return !hasName;
              })
          )
        );

        if (unknownIds.length) {
          unknownIds.forEach(async (mid) => {
            try {
              const mr = await fetch(`${API_BASE_URL}/api/asociacion-recetas/medicamento/${mid}`, { credentials: 'include', signal: ac.signal });
              if (!mr.ok) return;
              const jm = await mr.json();
              const resolved = resolveMedicationName({ ...jm, medicamento_id: mid });

              if (!mounted) return;
              setData((d) => {
                const nextPedidos = (d.pedidos || []).map((p) => (
                  Number(p?.medicamento_id) === mid
                    ? { ...p, ...mergeMedicationFields(p, jm), medicamento: resolved }
                    : p
                ));
                const nextRecetas = (d.recetas || []).map((r) => (
                  Number((d.pedidos || []).find((p) => Number(p.id) === Number(r.pedido_id))?.medicamento_id) === mid
                    ? { ...r, ...mergeMedicationFields(r, jm), medicamento: resolved, medicamento_id: mid }
                    : r
                ));
                const next = { ...d, pedidos: nextPedidos, recetas: nextRecetas };
                writeCache(next);
                return next;
              });
            } catch (_) { /* ignore */ }
          });
        }

        // Hydrate persisted recetas with controlled concurrency.
        const targetPedidos = pedidos.filter((p) => p && p.id && ['aprobado', 'en_tratamiento'].includes(String(p.estado)));
        const persistedByPedido = {};
        let cursor = 0;
        let lastPaintAt = 0;
        const maybePaint = () => {
          if (!mounted) return;
          const now = Date.now();
          if (now - lastPaintAt < 120) return;
          lastPaintAt = now;
          setData((d) => {
            const next = { ...d, recetas: applyPersistedRecetas(d.recetas || [], persistedByPedido) };
            writeCache(next);
            return next;
          });
        };

        const worker = async () => {
          while (mounted) {
            const idx = cursor;
            cursor += 1;
            if (idx >= targetPedidos.length) return;
            const p = targetPedidos[idx];
            try {
              const r = await fetch(`${API_BASE_URL}/api/asociacion-recetas/pedidos/${p.id}/recetas`, { credentials: 'include', signal: ac.signal });
              if (!r.ok) return;
              const jr = await r.json().catch(() => ({}));
              const persisted = Array.isArray(jr?.recetas) ? jr.recetas : (Array.isArray(jr) ? jr : []);
              if (persisted.length) {
                persistedByPedido[String(p.id)] = persisted;
                maybePaint();
              }
            } catch (_) { /* ignore */ }
          }
        };

        const concurrency = Math.min(12, Math.max(1, targetPedidos.length));
        await Promise.all(Array.from({ length: concurrency }, () => worker()));
        if (mounted) {
          setData((d) => {
            const next = { ...d, recetas: applyPersistedRecetas(d.recetas || [], persistedByPedido) };
            writeCache(next);
            return next;
          });
        }
      } catch (e) {
        if (e?.name !== 'AbortError') {
          console.error('Error cargando pedidos para RecetasEmitir:', e);
        }
      } finally {
        if (mounted) setInitialLoading(false);
      }
    })();

    return () => {
      mounted = false;
      ac.abort();
    };
  }, [RECETAS_EMITIR_CACHE_KEY]);

  // Accordion state for groups (closed by default)
  const [openGroups, setOpenGroups] = useState([]);
  const contentRefs = useRef({});
  const _initializedOpenRef = useRef(false);
  const [trackerOpen, setTrackerOpen] = useState(false);
  const [trackerPedidoId, setTrackerPedidoId] = useState(null);
  const [trackerReturnToHistorial, setTrackerReturnToHistorial] = useState(false);
  const [trackerEstado, setTrackerEstado] = useState(null);
  const [trackerHistory, setTrackerHistory] = useState([]);
  const trackerIntervalRef = useRef(null);
  const fechaHabilitadaRange = useMemo(() => {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const min = new Date(hoy.getTime());
    min.setDate(min.getDate() - EMISION_WINDOW_PASADO_DIAS);
    const max = new Date(hoy.getTime());
    max.setDate(max.getDate() + EMISION_WINDOW_FUTURO_DIAS);
    return { min, max };
  }, [EMISION_WINDOW_PASADO_DIAS, EMISION_WINDOW_FUTURO_DIAS]);

  function toggleGroup(id) {
    setOpenGroups(prev => (prev.includes(id) ? [] : [id]));
  }

  function closeTrackerDrawer() {
    setTrackerOpen(false);
    setTrackerPedidoId(null);
    if (trackerReturnToHistorial) {
      setModalHistorialGlobalOpen(true);
      setTrackerReturnToHistorial(false);
    }
  }

  function isFechaPrevistaHabilitada(fechaPrevista) {
    if (!fechaPrevista) return false;
    try {
      const d = new Date(`${fechaPrevista}T00:00:00`);
      if (Number.isNaN(d.getTime())) return false;
      d.setHours(0, 0, 0, 0);
      return d >= fechaHabilitadaRange.min && d <= fechaHabilitadaRange.max;
    } catch (e) {
      return false;
    }
  }

  function getDiasParaFecha(fechaPrevista) {
    if (!fechaPrevista) return null;
    try {
      const today = new Date();
      const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const d = new Date(`${fechaPrevista}T00:00:00`);
      if (Number.isNaN(d.getTime())) return null;
      return Math.round((d.getTime() - todayStart.getTime()) / (1000 * 60 * 60 * 24));
    } catch (e) {
      return null;
    }
  }

  function isRecetaRecordatorioHabilitado(receta) {
    if (!receta || String(receta.estado || '').toLowerCase() !== 'pendiente') return false;
    if (Number(receta.numero) === 1) return false;
    return isFechaPrevistaHabilitada(receta.fecha_prevista);
  }

  async function enviarMensajeVencimiento(receta, pedido) {
    if (!receta?.id || sendingMensajeRecetaId) return;
    const confirmResult = await fireConfirmDialog({
      title: 'Enviar recordatorio',
      html: buildCrmSwalBody({
        lead: 'Se enviará un recordatorio por WhatsApp al paciente.',
        items: ['Mensaje automático por el canal de recetas']
      }),
      confirmButtonText: 'Enviar',
      cancelButtonText: 'Cancelar'
    });
    if (!confirmResult.isConfirmed) return;

    setSendingMensajeRecetaId(receta.id);
    try {
      const endpoint = `${API_BASE_URL}/api/asociacion-recetas/recetas/${receta.id}/enviar-mensaje-vencimiento`;
      const res = await fetch(endpoint, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pedido_id: receta.pedido_id || pedido?.id || null,
          numero_receta: receta.numero || null,
          fecha_prevista: receta.fecha_prevista || null,
          estado: receta.estado || 'pendiente',
          paciente: receta.paciente || pedido?.paciente_nombre || pedido?.paciente || null,
          medicamento: resolveMedicationName(receta) || resolveMedicationName(pedido) || null,
          medico: pedido?._medico_nombre || pedido?.medico || null,
          posologia: pedido?.posologia || pedido?._posologia || null,
          dni: pedido?._dni_display || pedido?.paciente_dni || pedido?.solicitante_dni || null
        })
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(json?.message || `HTTP ${res.status}`);
      }
      Swal.fire({
        toast: true,
        position: 'bottom-end',
        icon: 'success',
        title: 'Mensaje enviado al paciente',
        showConfirmButton: false,
        timer: 3200,
        timerProgressBar: true,
        customClass: { popup: 'swal-toast-crm' }
      });
      setData((d) => ({
        ...d,
        recetas: (d.recetas || []).map((r) => (
          r.id === receta.id
            ? { ...r, recordatorio_vencimiento_enviado_at: new Date().toISOString(), respuesta_renovacion: null, respuesta_renovacion_at: null }
            : r
        ))
      }));
    } catch (e) {
      Swal.fire({
        toast: true,
        position: 'bottom-end',
        icon: 'error',
        title: e?.message || 'No se pudo enviar el mensaje',
        showConfirmButton: false,
        timer: 3800,
        timerProgressBar: true,
        customClass: { popup: 'swal-toast-crm' }
      });
    } finally {
      setSendingMensajeRecetaId(null);
    }
  }

  const hiddenPendientesCount = useMemo(
    () => (data.recetas || []).filter((r) => r.estado === 'pendiente' && !isFechaPrevistaHabilitada(r.fecha_prevista)).length,
    [data.recetas, fechaHabilitadaRange]
  );

  const groupedRecetas = useMemo(() => {
    const q = String(search || '').trim().toLowerCase();
    const allRecetas = data.recetas || [];
    let list = allRecetas;
    if (q) {
      list = list.filter(r => (
        String(r.paciente || '').toLowerCase().includes(q) ||
        String(r.medicamento || '').toLowerCase().includes(q) ||
        String(r.nro_externo || '').toLowerCase().includes(q)
      ));
    }

    const visiblePendingByPedido = new Set(
      list
        .filter((r) => r.estado === 'pendiente' && isFechaPrevistaHabilitada(r.fecha_prevista))
        .map((r) => String(r.pedido_id))
    );
    const map = {};
    list.forEach((r) => {
      const pid = String(r.pedido_id);
      const expanded = expandedHiddenPedidos.has(pid);
      const showGroup = visiblePendingByPedido.has(pid) || expanded;
      if (!showGroup) return;

      if (r.estado === 'pendiente' && !isFechaPrevistaHabilitada(r.fecha_prevista) && !expanded) return;

      if (!map[pid]) map[pid] = [];
      map[pid].push(r);
    });
    return Object.entries(map).map(([pid, recs]) => ({ pedido: data.pedidos.find(p => p.id === Number(pid)) || null, recetas: recs }));
  }, [data, search, fechaHabilitadaRange, expandedHiddenPedidos]);
  const pendientesCount = useMemo(
    () => groupedRecetas.reduce(
      (acc, grp) => acc + (grp.recetas || []).filter((r) => String(r?.estado || '').trim().toLowerCase() === 'pendiente').length,
      0
    ),
    [groupedRecetas]
  );
  const tratamientosActivos = useMemo(() => {
    const activePedidoIds = new Set(
      (data.recetas || [])
        .filter((r) => ['emitida_sistema_externo', 'confirmada_dispensa'].includes(String(r?.estado || '').trim().toLowerCase()))
        .map((r) => String(r?.pedido_id || ''))
        .filter(Boolean)
    );
    return activePedidoIds.size;
  }, [data.recetas]);

  const renovacionPacienteStats = useMemo(() => {
    const recetas = data.recetas || [];
    let esperando = 0;
    let confirmaronSi = 0;
    let respondieronNo = 0;
    for (const r of recetas) {
      const meta = resolveRenovacionPacienteMeta(r);
      if (!meta) continue;
      if (meta.tone === 'waiting') esperando += 1;
      else if (meta.tone === 'yes') confirmaronSi += 1;
      else if (meta.tone === 'no') respondieronNo += 1;
    }
    return { esperando, confirmaronSi, respondieronNo };
  }, [data.recetas]);

  useEffect(() => {
    if (renovacionPacienteStats.esperando <= 0) return undefined;
    let cancelled = false;
    const refreshRenovacionEstado = async () => {
      const pedidoIds = Array.from(
        new Set(
          (data.recetas || [])
            .filter((r) => {
              const meta = resolveRenovacionPacienteMeta(r);
              return meta?.tone === 'waiting';
            })
            .map((r) => Number(r.pedido_id))
            .filter((id) => Number.isFinite(id))
        )
      );
      if (!pedidoIds.length) return;
      const persistedByPedido = {};
      await Promise.all(pedidoIds.map(async (pid) => {
        try {
          const r = await fetch(`${API_BASE_URL}/api/asociacion-recetas/pedidos/${pid}/recetas`, { credentials: 'include' });
          if (!r.ok) return;
          const jr = await r.json().catch(() => ({}));
          const persisted = Array.isArray(jr?.recetas) ? jr.recetas : [];
          if (persisted.length) persistedByPedido[String(pid)] = persisted;
        } catch (_) { /* ignore */ }
      }));
      if (cancelled || !Object.keys(persistedByPedido).length) return;
      setData((d) => {
        const mergePersisted = (baseRecetas) => baseRecetas.map((rec) => {
          const persisted = persistedByPedido[String(rec.pedido_id)] || [];
          const match = pickPersistedRecetaMatch(persisted, rec.numero);
          return mergeRecetaFromPersisted(rec, match);
        });
        const next = { ...d, recetas: mergePersisted(d.recetas || []) };
        try {
          sessionStorage.setItem(RECETAS_EMITIR_CACHE_KEY, JSON.stringify({ at: Date.now(), data: next }));
        } catch (_) { /* ignore */ }
        return next;
      });
    };
    const timer = setInterval(refreshRenovacionEstado, 12000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [renovacionPacienteStats.esperando, data.recetas, RECETAS_EMITIR_CACHE_KEY]);

  const totalPages = Math.max(1, Math.ceil(groupedRecetas.length / itemsPerPage));
  useEffect(() => { if (page > totalPages) setPage(1); }, [groupedRecetas.length, itemsPerPage]);
  const pageItems = useMemo(() => groupedRecetas.slice((page - 1) * itemsPerPage, page * itemsPerPage), [groupedRecetas, page, itemsPerPage]);

  function resolveSecuencialEmisionBlock(receta) {
    if (!receta) return null;
    const pid = String(receta.pedido_id || '');
    const numeroActual = Number(receta.numero);
    if (!pid || !Number.isFinite(numeroActual)) return null;

    const prevPendiente = (data.recetas || [])
      .filter((r) => String(r?.pedido_id || '') === pid)
      .some((r) => {
        const n = Number(r?.numero);
        if (!Number.isFinite(n) || n >= numeroActual) return false;
        return String(r?.estado || '').trim().toLowerCase() === 'pendiente';
      });

    if (!prevPendiente) return null;

    // Find the earliest pending number to give a clear hint.
    const firstPendiente = (data.recetas || [])
      .filter((r) => String(r?.pedido_id || '') === pid)
      .filter((r) => String(r?.estado || '').trim().toLowerCase() === 'pendiente')
      .map((r) => Number(r?.numero))
      .filter((n) => Number.isFinite(n))
      .sort((a, b) => a - b)[0];

    const firstLabel = Number.isFinite(firstPendiente) ? `Receta ${firstPendiente}` : 'la receta anterior';
    return `Emisión secuencial: primero emití ${firstLabel}.`;
  }

  // Open the first group (first page) once on initial render
  useEffect(() => {
    if (_initializedOpenRef.current) return;
    if (pageItems && pageItems.length > 0) {
      setOpenGroups([0]);
      _initializedOpenRef.current = true;
    }
  }, [pageItems]);

  const closeTemplateModal = useCallback(() => {
    setTemplateModalOpen(false);
    setSelectedTemplate(null);
    setTemplateParamValues([]);
    setTemplatesError('');
  }, []);

  const openTemplatePicker = useCallback(async (conversationId) => {
    if (!conversationId) return;
    setTemplateConversationId(conversationId);
    setTemplateModalOpen(true);
    setSelectedTemplate(null);
    setTemplateParamValues([]);
    setTemplatesError('');
    setTemplatesLoading(true);
    try {
      const tplData = await fetchConversationTemplates(conversationId);
      setTemplates(Array.isArray(tplData?.templates) ? tplData.templates : []);
    } catch (err) {
      setTemplatesError(err?.message || 'No se pudieron cargar las plantillas');
      setTemplates([]);
    } finally {
      setTemplatesLoading(false);
    }
  }, []);

  const handlePickTemplate = useCallback((tpl) => {
    setSelectedTemplate(tpl);
    const count = Number(tpl?.params_count) || 0;
    setTemplateParamValues(Array.from({ length: count }, () => ''));
  }, []);

  const fillTemplateBody = useCallback((body, values) => {
    return String(body || '').replace(/\{\{\s*(\d+)\s*\}\}/g, (match, num) => {
      const idx = Number(num) - 1;
      const val = values?.[idx];
      return val != null && String(val).trim() ? String(val) : match;
    });
  }, []);

  const handleSendTemplate = useCallback(async () => {
    if (!templateConversationId || !selectedTemplate || sendingTemplate) return;
    const count = Number(selectedTemplate.params_count) || 0;
    if (count > 0 && templateParamValues.some((v) => !String(v || '').trim())) {
      setTemplatesError('Completá todos los campos de la plantilla.');
      return;
    }
    const processedParams = {};
    for (let i = 0; i < count; i += 1) {
      processedParams[String(i + 1)] = String(templateParamValues[i] || '');
    }
    const content = fillTemplateBody(selectedTemplate.body, templateParamValues);
    setSendingTemplate(true);
    setTemplatesError('');
    try {
      await sendConversationTemplate(templateConversationId, {
        name: selectedTemplate.name,
        language: selectedTemplate.language,
        category: selectedTemplate.category || undefined,
        namespace: selectedTemplate.namespace || undefined,
        processed_params: processedParams,
        content
      });
      closeTemplateModal();
    } catch (err) {
      setTemplatesError(err?.message || 'No se pudo enviar la plantilla');
    } finally {
      setSendingTemplate(false);
    }
  }, [templateConversationId, selectedTemplate, sendingTemplate, templateParamValues, fillTemplateBody, closeTemplateModal]);

  function abrirEmitir(id) {
    const r = data.recetas.find(x => x.id === id);
    if (!r || !isFechaPrevistaHabilitada(r.fecha_prevista)) return;
    const blockReason = resolveSecuencialEmisionBlock(r);
    if (blockReason) {
      Swal.fire({
        toast: true,
        position: 'bottom-end',
        icon: 'warning',
        title: blockReason,
        showConfirmButton: false,
        timer: 3800,
        timerProgressBar: true,
        customClass: { popup: 'swal-toast-crm' }
      });
      return;
    }
    const pedido = (data.pedidos || []).find((p) => Number(p.id) === Number(r.pedido_id));
    setConfirmandoEmision(false);
    setSelectedRecetaId(id);
    setEmitirNro(`REC-EXT-${String(id).padStart(3, '0')}`);
    setEmitirUrl(String(r.receta_url || '').trim());
    setEmitStep('whatsapp');
    setEmitWa(Boolean(pedido?.disparar_webhook_emitir_receta));
    setModalEmitirOpen(true);
  }

  async function confirmarEmision() {
    const r = (data.recetas || []).find((x) => x.id === selectedRecetaId);
    const blockReason = resolveSecuencialEmisionBlock(r);
    if (blockReason) {
      Swal.fire({
        toast: true,
        position: 'bottom-end',
        icon: 'warning',
        title: blockReason,
        showConfirmButton: false,
        timer: 3800,
        timerProgressBar: true,
        customClass: { popup: 'swal-toast-crm' }
      });
      return;
    }
    setModalEmitirOpen(false);
    setConfirmandoEmision(true);
    (async () => {
      try {
        const endpoint = `${API_BASE_URL}/api/asociacion-recetas/recetas/${selectedRecetaId}/emitir`;
        const res = await fetch(endpoint, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ nro_externo: emitirNro, receta_url: emitirUrl.trim() || null, disparar_webhook_emitir_receta: emitWa ? 1 : 0 })
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json().catch(() => null) || {};
        const updated = json.receta || json;
        setData(d => {
          const recetas = (d.recetas || []).map((r) => {
            if (r.id !== selectedRecetaId) return r;
            return {
              ...r,
              estado: (updated.estado || 'emitida_sistema_externo'),
              nro_externo: (updated.nro_externo || updated.nro_receta_externo || emitirNro),
              receta_url: (updated.receta_url || emitirUrl.trim() || ''),
              fecha_emitida: (updated.fecha_emitida || new Date().toLocaleDateString('es-AR')),
              recordatorio_vencimiento_enviado_at: null,
              respuesta_renovacion: null,
              respuesta_renovacion_at: null,
              respuesta_renovacion_mensaje: null
            };
          });
          const pedidos = (d.pedidos || []).map(p => {
            const pid = p.id || p.pedido_id;
            let fromRecPid = null;
            if (String(selectedRecetaId).includes('-')) fromRecPid = String(selectedRecetaId).split('-')[0];
            if (String(pid) === String(fromRecPid) || String(pid) === String((updated.pedido_id || updated.pedido || updated.pedidoId))) {
              return { ...p, recetas_generadas: (Number(p.recetas_generadas || 0) + 1) };
            }
            return p;
          });
          return { ...d, recetas, pedidos };
        });
        const emittedReceta = (data.recetas || []).find((x) => x.id === selectedRecetaId);
        const emittedPid = String(emittedReceta?.pedido_id || (String(selectedRecetaId).includes('-') ? selectedRecetaId.split('-')[0] : ''));
        const quedanPendientesEnVentana = (data.recetas || []).some((r) => (
          String(r.pedido_id) === emittedPid
          && r.id !== selectedRecetaId
          && String(r.estado || '').trim().toLowerCase() === 'pendiente'
          && isFechaPrevistaHabilitada(r.fecha_prevista)
        ));
        if (emittedPid && !quedanPendientesEnVentana) {
          setExpandedHiddenPedidos((prev) => {
            if (!prev.has(emittedPid)) return prev;
            const next = new Set(prev);
            next.delete(emittedPid);
            return next;
          });
        }
        setModalEmitirOpen(false);
        setEmitirUrl('');
        const wh = json?.webhook || {};
        const whMsg = wh.solicitado === false
          ? 'Sin aviso WhatsApp (desactivado en la aprobación).'
          : (wh.enviado
            ? 'WhatsApp de emisión enviado al paciente.'
            : (wh.solicitado ? `WhatsApp no enviado${wh.error ? `: ${wh.error}` : ''}` : ''));
        Swal.fire({
          toast: true,
          position: 'bottom-end',
          icon: wh.solicitado && !wh.enviado ? 'warning' : 'success',
          html: `
            <div style="font-weight:700;color:#253342">Receta registrada como emitida</div>
            ${whMsg ? `<div style="font-size:12px;color:#516F90;margin-top:6px">${whMsg}</div>` : ''}
          `,
          showConfirmButton: false,
          timer: 4000,
          timerProgressBar: true,
          customClass: { popup: 'swal-toast-crm' }
        });
      } catch (e) {
        // fallback local update
        setData(d => ({
          ...d,
          recetas: d.recetas.map((r) => {
            if (r.id !== selectedRecetaId) return r;
            return {
              ...r,
              estado: 'emitida_sistema_externo',
              nro_externo: emitirNro,
              receta_url: emitirUrl.trim(),
              fecha_emitida: new Date().toLocaleDateString('es-AR'),
              recordatorio_vencimiento_enviado_at: null,
              respuesta_renovacion: null,
              respuesta_renovacion_at: null,
              respuesta_renovacion_mensaje: null
            };
          })
        }));
        setModalEmitirOpen(false);
        setEmitirUrl('');
        Swal.fire({ toast: true, position: 'bottom-end', icon: 'warning', title: 'Receta marcada localmente como emitida (error al persistir)', showConfirmButton: false, timer: 3500, timerProgressBar: true, customClass: { popup: 'swal-toast-crm' } });
      } finally {
        setConfirmandoEmision(false);
      }
    })();
  }

  const styles = {
    container: { padding: isMobile ? '0.75rem' : '1.25rem', display: 'grid', gap: 12 },
    pageHeader: {
      background: '#fff',
      border: '1px solid #DFE3EB',
      borderRadius: 12,
      padding: isMobile ? '10px 12px' : '10px 16px',
      minHeight: 56,
      display: 'flex',
      alignItems: isMobile ? 'flex-start' : 'center',
      justifyContent: 'space-between',
      flexDirection: isMobile ? 'column' : 'row',
      boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
      gap: 8
    },
    kpiGrid: { display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,minmax(0,1fr))' : (isTablet ? 'repeat(2,minmax(0,1fr))' : 'repeat(4,1fr)'), gap: 10, marginBottom: 6 },
    statNum: { fontFamily: 'monospace', fontSize: 24, fontWeight: 700, color: '#253342' },
    modalBackdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' },
    drawerOverlay: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.32)', backdropFilter: 'blur(2px)', zIndex: 240 },
    drawer: {
      position: 'fixed',
      left: 0,
      top: 0,
      bottom: 0,
      width: isMobile ? '94vw' : 'min(92vw, 460px)',
      background: 'linear-gradient(180deg,#ffffff 0%,#f8fbff 100%)',
      borderRight: '1px solid #dbe5f2',
      boxShadow: '20px 0 46px rgba(2,8,20,.26)',
      zIndex: 241,
      display: 'flex',
      flexDirection: 'column',
      transition: 'transform .34s cubic-bezier(.22,.9,.22,1)'
    },
    drawerHead: {
      padding: '14px 14px 12px',
      borderBottom: '1px solid #e8eef7',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 12
    },
    drawerBody: { padding: 14, overflowY: 'auto', display: 'grid', gap: 10 },
    drawerClose: {
      width: 30,
      height: 30,
      borderRadius: 999,
      border: '1px solid #d3deec',
      background: '#fff',
      cursor: 'pointer'
    },
    drawerFoot: {
      borderTop: '1px solid #e8eef7',
      padding: '12px 14px 14px',
      marginTop: 'auto',
      background: 'rgba(255,255,255,.96)'
    },
    modal: { background: 'var(--hs-white)', borderRadius: 12, padding: isMobile ? 14 : 20, width: isMobile ? '94vw' : 520, maxWidth: '94vw', maxHeight: '92vh', overflowY: 'auto', boxSizing: 'border-box', boxShadow: 'var(--sm)', color: 'var(--hs-text)' },
    formInput: { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--hs-border)', fontSize: 14, boxSizing: 'border-box' },
    loadingBox: {
      border: '1px solid #DFE3EB',
      borderRadius: 12,
      padding: 28,
      textAlign: 'center',
      color: '#7C98B6',
      background: '#fff',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 10,
      minHeight: 180,
      boxShadow: '0 1px 2px rgba(0,0,0,.05)'
    },
    spinner: {
      width: 34,
      height: 34,
      borderRadius: '50%',
      border: '3px solid #DFE3EB',
      borderTopColor: '#2365AB',
      animation: 'recetas-emitir-spin .8s linear infinite'
    }
  };

  function estadoBadge(recetaOrEstado) {
    return <CrmStatusBadgeFrom badge={resolveRecetaStatusBadge(recetaOrEstado)} />;
  }

  function formatPatientHeader(fullName, dni) {
    const formattedDni = String(dni || '')
      .replace(/\D/g, '')
      .replace(/^0+/, '')
      .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    if (!fullName) return formattedDni ? `DNI ${formattedDni}` : '';
    const parts = String(fullName).trim().split(/\s+/);
    if (parts.length === 1) {
      return (parts[0] || '').toUpperCase() + (formattedDni ? ` · DNI ${formattedDni}` : '');
    }
    const last = parts.pop();
    const rest = parts.join(' ');
    return `${rest.toUpperCase()}, ${last.toUpperCase()}${formattedDni ? ` · DNI ${formattedDni}` : ''}`;
  }

  function formatMedicoTitulo(rawName) {
    const name = String(rawName || '').trim();
    if (!name) return '';
    const lower = name.toLowerCase();
    if (lower.startsWith('dra. ') || lower.startsWith('dra ') || lower.startsWith('doctora ')) return name;
    if (lower.startsWith('dr. ') || lower.startsWith('dr ') || lower.startsWith('doctor ')) return name;
    const parts = name.split(/\s+/).filter(Boolean);
    const givenName = parts.length ? parts[parts.length - 1] : name;
    const normalized = givenName
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
    const female = normalized.endsWith('a');
    return `${female ? 'Dra.' : 'Dr.'} ${name}`;
  }

  function formatMedicamentoLabel(rawName) {
    const name = String(rawName || '').trim();
    if (!name) return 'Medicamento no informado';
    return name
      .split(' ')
      .filter(Boolean)
      .map((word) => {
        if (word.length <= 1) return word.toUpperCase();
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(' ');
  }

  function toggleMedicationDetails(detailKey) {
    if (!detailKey) return;
    setOpenMedicationDetails((prev) => (prev === detailKey ? '' : detailKey));
  }

  function renderEyeIcon(isOpen) {
    const common = {
      width: 16,
      height: 16,
      viewBox: '0 0 24 24',
      fill: 'none',
      stroke: 'currentColor',
      strokeWidth: 1.9,
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
      'aria-hidden': true
    };
    if (isOpen) {
      return (
        <svg {...common}>
          <path d="M4 4 20 20" />
          <path d="M10.7 6.2A10.8 10.8 0 0 1 12 6c6.5 0 10 6 10 6a18 18 0 0 1-3.3 4.2" />
          <path d="M6.2 8.5A18.5 18.5 0 0 0 2 12s3.5 6 10 6c.7 0 1.4-.1 2-.2" />
        </svg>
      );
    }
    return (
      <svg {...common}>
        <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6Z" />
        <circle cx="12" cy="12" r="2.8" />
      </svg>
    );
  }

  function renderMedicationSummary(source, detailKey, options = {}) {
    const { openAbove = false } = options;
    const medication = buildMedicationDisplay(source);
    const hasDetails = Boolean(medication.laboratoryLabel || medication.genericLine || medication.meta.length);
    const isOpen = detailKey ? openMedicationDetails === detailKey : false;
    return (
      <div data-medication-detail-root="true" className="crm-re-med-detail-root">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, minWidth: 0 }}>
          <div style={{ fontSize: 12, color: '#0f172a', fontWeight: 800, lineHeight: 1.15, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {formatMedicamentoLabel(medication.title)}
          </div>
          {hasDetails ? (
            <button
              type="button"
              onClick={() => toggleMedicationDetails(detailKey)}
              aria-label={isOpen ? 'Ocultar detalle del medicamento' : 'Ver detalle del medicamento'}
              title={isOpen ? 'Ocultar detalle' : 'Ver detalle'}
              style={{
                width: 28,
                height: 28,
                flexShrink: 0,
                border: '1px solid #d6e2f0',
                background: isOpen ? 'linear-gradient(180deg,#eff6ff,#dbeafe)' : 'linear-gradient(180deg,#ffffff,#f8fbff)',
                color: '#1d4ed8',
                borderRadius: 999,
                padding: 0,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: isOpen ? '0 8px 18px rgba(37,99,235,.18)' : '0 4px 10px rgba(15,23,42,.08)'
              }}
            >
              {renderEyeIcon(isOpen)}
            </button>
          ) : null}
        </div>
        {isOpen ? (
          <div
            className={`crm-re-med-detail-popover${openAbove ? ' crm-re-med-detail-popover--above' : ''}`}
          >
            <div className="crm-re-med-detail-popover__arrow" aria-hidden="true" />
            <div style={{ display: 'grid', gap: 2, paddingBottom: 7, borderBottom: '1px solid rgba(148,163,184,.18)' }}>
              <div style={{ fontSize: 9, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.08em' }}>Detalle del medicamento</div>
              <div style={{ fontSize: 12, fontWeight: 800, color: '#0f172a', lineHeight: 1.15 }}>{formatMedicamentoLabel(medication.title)}</div>
            </div>
            <div style={{ display: 'grid', gap: 6 }}>
              {medication.laboratoryLabel ? (
                <div style={{ fontSize: 10, color: '#1d4ed8', fontWeight: 800, background: 'rgba(219,234,254,.55)', border: '1px solid rgba(191,219,254,.85)', padding: '5px 7px', borderRadius: 9 }}>{medication.laboratoryLabel}</div>
              ) : null}
              {medication.genericLine ? (
                <div style={{ fontSize: 10, color: '#334155', fontWeight: 700, background: 'rgba(248,250,252,.95)', border: '1px solid rgba(226,232,240,.95)', padding: '5px 7px', borderRadius: 9 }}>{medication.genericLine}</div>
              ) : null}
              {medication.meta.length ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {medication.meta.map((item) => (
                    <div key={`${detailKey || medication.title}-${item}`} style={{ fontSize: 10, color: '#475569', fontWeight: 700, background: '#fff', border: '1px solid rgba(203,213,225,.95)', padding: '4px 7px', borderRadius: 999 }}>
                      {item}
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  function estadoLabel(estado) {
    if (estado === 'pendiente_call_center') return 'Sin gestionar';
    if (estado === 'pedido_generado_farmacia') return 'Pedido en farmacia';
    if (estado === 'en_preparacion') return 'En preparación';
    if (estado === 'en_camino') return 'En camino';
    if (estado === 'entregado') return 'Entregado';
    if (estado === 'cancelado') return 'Cancelado';
    return estado;
  }

  function labelizeState(st) {
    if (!st) return '';
    const map = {
      'pendiente_call_center': 'Sin gestionar',
      'pedido_generado_farmacia': 'Pedido en farmacia',
      'en_preparacion': 'En preparación',
      'en_camino': 'En camino',
      'entregado': 'Entregado',
      'cancelado': 'Cancelado'
    };
    if (map[st]) return map[st];
    const s = String(st).replaceAll ? String(st).replaceAll('_', ' ') : String(st);
    return s.charAt(0).toUpperCase() + s.slice(1);
  }

  function formatDateTs(ts) {
    if (!ts) return '';
    try {
      const d = new Date(ts);
      if (Number.isNaN(d.getTime())) return String(ts);
      return d.toLocaleString('es-AR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
    } catch (e) { return String(ts); }
  }

  function trackerStateIcon(st) {
    const common = { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', xmlns: 'http://www.w3.org/2000/svg', style: { display: 'block' } };
    if (st === 'pedido_generado_farmacia') {
      return (
        <svg {...common}>
          <path d="M3 7.5L12 3l9 4.5-9 4.5L3 7.5Z" stroke="currentColor" strokeWidth="1.8" />
          <path d="M3 7.5V16.5L12 21V12" stroke="currentColor" strokeWidth="1.8" />
          <path d="M21 7.5V16.5L12 21" stroke="currentColor" strokeWidth="1.8" />
        </svg>
      );
    }
    if (st === 'en_preparacion') {
      return (
        <svg {...common}>
          <rect x="4" y="6" width="16" height="4" rx="2" stroke="currentColor" strokeWidth="1.8" />
          <rect x="4" y="14" width="16" height="4" rx="2" stroke="currentColor" strokeWidth="1.8" />
        </svg>
      );
    }
    if (st === 'en_camino') {
      return (
        <svg {...common}>
          <path d="M3 7h11v7H3V7Z" stroke="currentColor" strokeWidth="1.8" />
          <path d="M14 9h3l3 3v2h-6V9Z" stroke="currentColor" strokeWidth="1.8" />
          <circle cx="7" cy="16.5" r="1.5" fill="currentColor" />
          <circle cx="17" cy="16.5" r="1.5" fill="currentColor" />
        </svg>
      );
    }
    if (st === 'entregado') {
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
          <path d="M8 12.5l2.4 2.4L16 9.8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    }
    if (st === 'cancelado') {
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
          <path d="M9 9l6 6M15 9l-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      );
    }
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
        <path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  // Tracker: fetch estado of pedido_farmacia entries periodically while tracker open
  useEffect(() => {
    async function refreshTracker() {
      try {
        if (!trackerPedidoId) return;
        // fetch current estado from pedidos_farmacia list
        const r = await fetch(`${API_BASE_URL}/api/asociacion-recetas/pedidos_farmacia`, { credentials: 'include' });
        if (r.ok) {
          const rows = await r.json();
          const found = Array.isArray(rows) ? rows.find(rr => String(rr.receta_id) === String(trackerPedidoId) || String(rr.id) === String(trackerPedidoId) || String(rr.pedido_id) === String(trackerPedidoId)) : null;
          if (found) setTrackerEstado(found.estado || null);
        }
        // fetch history for this pedido
        try {
          const rh = await fetch(`${API_BASE_URL}/api/asociacion-recetas/pedidos-farmacia/${trackerPedidoId}/hist`, { credentials: 'include' });
          if (rh.ok) {
            const jh = await rh.json();
            setTrackerHistory(Array.isArray(jh.history) ? jh.history : (jh || []));
          }
        } catch (err) { /* ignore history fetch errors */ }
      } catch (e) {
        // ignore
      }
    }
    if (trackerOpen) {
      refreshTracker();
      trackerIntervalRef.current = setInterval(refreshTracker, 4000);
    }
    return () => {
      if (trackerIntervalRef.current) { clearInterval(trackerIntervalRef.current); trackerIntervalRef.current = null; }
      setTrackerEstado(null);
    };
  }, [trackerOpen, trackerPedidoId, data.pedidos]);

  const TRACK_STATES = ['pendiente_call_center', 'pedido_generado_farmacia', 'en_preparacion', 'en_camino', 'entregado', 'cancelado'];

  const historialPacientes = useMemo(() => {
    const map = new Map();
    (data.pedidos || []).forEach((p) => {
      const dni = String(p?.dni_normalizado || p?.paciente_dni || p?.solicitante_dni || '').replace(/\D/g, '');
      if (!dni) return;
      const nombre = String(p?.paciente_nombre || p?.paciente || '').trim() || `DNI ${dni}`;
      if (!map.has(dni)) map.set(dni, { dni, nombre });
    });
    return Array.from(map.values()).sort((a, b) => String(a.nombre).localeCompare(String(b.nombre), 'es', { sensitivity: 'base' }));
  }, [data.pedidos]);

  const filteredHistorialPacientes = useMemo(() => {
    const q = String(historialGlobalPacienteSearch || '').trim().toLowerCase();
    if (!q) return historialPacientes;
    return historialPacientes.filter((p) => (
      String(p?.nombre || '').toLowerCase().includes(q) || String(p?.dni || '').includes(q)
    ));
  }, [historialPacientes, historialGlobalPacienteSearch]);

  const historialGlobalItems = useMemo(() => {
    if (!historialGlobalDni) return [];
    const pedidosById = new Map((data.pedidos || []).map((p) => [String(p.id), p]));
    return (data.recetas || [])
      .filter((r) => {
        const p = pedidosById.get(String(r?.pedido_id));
        const dni = String(p?.dni_normalizado || p?.paciente_dni || p?.solicitante_dni || '').replace(/\D/g, '');
        return dni === String(historialGlobalDni);
      })
      .map((r) => {
        const p = pedidosById.get(String(r?.pedido_id));
        return {
          id: r.id,
          numero: r.numero,
          pedido_id: r.pedido_id || p?.id || null,
          pedido_farmacia_id: r.pedido_farmacia_id || null,
          paciente: r.paciente || p?.paciente_nombre || p?.paciente || '',
          dni: p?.dni_normalizado || p?.paciente_dni || p?.solicitante_dni || '',
          medicamento: resolveMedicationName(r) || resolveMedicationName(p) || '—',
          estado: r.estado || '',
          medico: p?.medico || p?._medico_nombre || '',
          notas: p?.notas || '',
          posologia: p?.posologia || p?._posologia || '',
          fecha_prevista: r.fecha_prevista || null,
          fecha_emitida: r.fecha_emitida || null,
          nro_externo: r.nro_externo || ''
        };
      });
  }, [data.pedidos, data.recetas, historialGlobalDni]);

  const filteredHistorialGlobalItems = useMemo(() => {
    const q = String(historialGlobalSearch || '').trim().toLowerCase();
    const toMs = (v) => {
      if (!v) return null;
      const d = new Date(v);
      return Number.isNaN(d.getTime()) ? null : d.getTime();
    };
    const desdeMs = toMs(historialGlobalDesde);
    const hastaMs = toMs(historialGlobalHasta);
    let rows = historialGlobalItems.filter((h) => {
      if (!q) return true;
      return (
        String(h.medicamento || '').toLowerCase().includes(q)
        || String(h.estado || '').toLowerCase().includes(q)
        || String(h.medico || '').toLowerCase().includes(q)
        || String(h.notas || '').toLowerCase().includes(q)
        || String(h.nro_externo || '').toLowerCase().includes(q)
      );
    });
    if (historialGlobalEstado !== 'todos') rows = rows.filter((h) => String(h.estado) === String(historialGlobalEstado));
    rows = rows.filter((h) => {
      const tsRaw = h.fecha_emitida || h.fecha_prevista || null;
      const ts = tsRaw ? new Date(String(tsRaw).replace(' ', 'T')).getTime() : 0;
      if (desdeMs && ts < desdeMs) return false;
      if (hastaMs && ts > (hastaMs + 24 * 60 * 60 * 1000 - 1)) return false;
      return true;
    });
    rows.sort((a, b) => {
      const aIsEmitida = String(a.estado) === 'emitida_sistema_externo';
      const bIsEmitida = String(b.estado) === 'emitida_sistema_externo';
      if (aIsEmitida !== bIsEmitida) return aIsEmitida ? -1 : 1;

      const toTs = (v) => (v ? new Date(String(v).replace(' ', 'T')).getTime() : 0);
      const aEmitTs = toTs(a.fecha_emitida);
      const bEmitTs = toTs(b.fecha_emitida);
      const aPrevTs = toTs(a.fecha_prevista);
      const bPrevTs = toTs(b.fecha_prevista);

      if (aIsEmitida && bIsEmitida) {
        if (historialGlobalOrder === 'fecha_asc') return aEmitTs - bEmitTs;
        return bEmitTs - aEmitTs;
      }

      // Pendientes/no emitidas: mostrar primero la fecha prevista mas cercana.
      if (historialGlobalOrder === 'fecha_asc') return bPrevTs - aPrevTs;
      return aPrevTs - bPrevTs;
    });
    return rows;
  }, [historialGlobalItems, historialGlobalSearch, historialGlobalDesde, historialGlobalHasta, historialGlobalEstado, historialGlobalOrder]);

  const totalHistorialGlobalPages = Math.max(1, Math.ceil(filteredHistorialGlobalItems.length / historialGlobalItemsPerPage));
  useEffect(() => {
    if (historialGlobalPage > totalHistorialGlobalPages) setHistorialGlobalPage(1);
  }, [historialGlobalPage, totalHistorialGlobalPages]);
  useEffect(() => {
    setHistorialGlobalPage(1);
  }, [historialGlobalSearch, historialGlobalDesde, historialGlobalHasta, historialGlobalEstado, historialGlobalOrder, historialGlobalDni, historialGlobalItemsPerPage]);
  const pageHistorialGlobalItems = useMemo(() => (
    filteredHistorialGlobalItems.slice((historialGlobalPage - 1) * historialGlobalItemsPerPage, historialGlobalPage * historialGlobalItemsPerPage)
  ), [filteredHistorialGlobalItems, historialGlobalPage, historialGlobalItemsPerPage]);

  return (
    <section className={`crm-gd-page crm-gd-deriv-page crm-re-page crm-pbody${openMedicationDetails ? ' crm-re-page--med-detail-open' : ''}`}>
      <div className="crm-gd-deriv-shell">
        <div className="crm-gd-deriv-top">
          <div className="crm-gd-kpi-grid" style={isMobile ? { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' } : undefined}>
            <div className="crm-gd-kpi-card">
              <div className="crm-gd-kpi-card__num">{pendientesCount}</div>
              <div className="crm-gd-kpi-card__label">Pendientes emisión</div>
            </div>
            <div className="crm-gd-kpi-card">
              <div className="crm-gd-kpi-card__num">{emitidas.length}</div>
              <div className="crm-gd-kpi-card__label">Emitidas</div>
            </div>
            <div className="crm-gd-kpi-card">
              <div className="crm-gd-kpi-card__num">{data.recetas.length}</div>
              <div className="crm-gd-kpi-card__label">Total recetas</div>
            </div>
            <div className="crm-gd-kpi-card">
              <div className="crm-gd-kpi-card__num">{tratamientosActivos}</div>
              <div className="crm-gd-kpi-card__label">Tratamientos activos</div>
            </div>
          </div>

          <div className="crm-gd-toolbar">
          <div className="crm-gd-toolbar__search">
            <CwListSearch
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Buscar paciente, medicamento o nro externo…"
            />
          </div>
          <span className="crm-re-toolbar-meta">
            {initialLoading ? 'Preparando…' : `${groupedRecetas.length} resultados`}
            {renovacionPacienteStats.esperando > 0 ? (
              <span className="crm-re-renov-toolbar-chip crm-re-renov-toolbar-chip--waiting">
                {renovacionPacienteStats.esperando} esperando WhatsApp
              </span>
            ) : null}
            {renovacionPacienteStats.confirmaronSi > 0 ? (
              <span className="crm-re-renov-toolbar-chip crm-re-renov-toolbar-chip--yes">
                {renovacionPacienteStats.confirmaronSi} quieren renovar
              </span>
            ) : null}
          </span>
          <div className="crm-gd-toolbar__actions">
            <div className="crm-re-toolbar-field">
              <label htmlFor="re-items-per-page">Mostrar</label>
              <select
                id="re-items-per-page"
                className="crm-re-select"
                value={itemsPerPage}
                onChange={(e) => { setItemsPerPage(Number(e.target.value)); setPage(1); }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>
            <button
              type="button"
              className="crm-gd-btn crm-gd-btn--ghost"
              onClick={() => {
                setHistorialGlobalDni('');
                setHistorialGlobalSearch('');
                setHistorialGlobalPacienteSearch('');
                setHistorialGlobalDesde('');
                setHistorialGlobalHasta('');
                setHistorialGlobalEstado('todos');
                setHistorialGlobalOrder('fecha_desc');
                setHistorialGlobalPage(1);
                setModalHistorialGlobalOpen(true);
              }}
            >
              Historial global
            </button>
          </div>
          </div>
        </div>

        <div className="crm-gd-deriv-scroll">
          {initialLoading ? (
            <div className="crm-gd-loading" role="status" aria-live="polite">
              <div className="crm-gd-spinner" aria-hidden="true" />
              <div className="crm-gd-loading__title">Preparando emisión de recetas</div>
              <div style={{ fontSize: 12, color: '#64748b' }}>
                Cargando pedidos y estado de recetas…
              </div>
            </div>
          ) : groupedRecetas.length === 0 ? (
            <div className="crm-gd-empty crm-re-empty-card">No hay recetas</div>
          ) : (
            <div className="crm-gd-list crm-re-list">
            {pageItems.map((grp, idx) => {
              const totalRecetasInGroup = (data.recetas || []).filter((r) => Number(r.pedido_id) === Number(grp.pedido?.id)).length;
              const emittedCount = (grp.recetas || []).filter(r => r.estado && r.estado !== 'pendiente').length;
              const totalPendingCount = (data.recetas || []).filter((r) => (
                Number(r.pedido_id) === Number(grp.pedido?.id) && r.estado === 'pendiente'
              )).length;
              const visiblePendingCount = (grp.recetas || []).filter(r => r.estado === 'pendiente').length;
              const firstPendingVisibleIdx = (grp.recetas || []).findIndex((r) => r.estado === 'pendiente');
              const toggleButtonRowIdx = firstPendingVisibleIdx >= 0 ? firstPendingVisibleIdx : 0;
              const hiddenPendientesInGroup = (data.recetas || []).filter((r) => (
                Number(r.pedido_id) === Number(grp.pedido?.id) &&
                r.estado === 'pendiente' &&
                !isFechaPrevistaHabilitada(r.fecha_prevista)
              )).length;
              const titularDni = String(grp.pedido?.solicitante_dni || '').replace(/\D/g, '').replace(/^0+/, '');
              const pacienteDni = String(grp.pedido?.paciente_dni || '').replace(/\D/g, '').replace(/^0+/, '');
              const isFamilyGroup = Boolean(titularDni && pacienteDni && titularDni !== pacienteDni);
              const groupPid = String(grp.pedido?.id || grp.recetas?.[0]?.pedido_id || '');
              const groupExpanded = expandedHiddenPedidos.has(groupPid);
              const isLastVisibleCard = idx === pageItems.length - 1;
              const isFirstVisibleCard = idx === 0;
              const openMedDetailAbove = isLastVisibleCard && !isFirstVisibleCard;
              const groupTheme = emittedCount > 0 && totalPendingCount === 0
                ? { accent: '#00BDA5', soft: 'rgba(0,189,165,.12)', badgeBg: 'linear-gradient(145deg,#ecfeff,#ccfbf1)', badgeColor: '#0f766e', badgeBorder: 'rgba(20,184,166,.28)' }
                : totalPendingCount > 0 && emittedCount === 0
                  ? { accent: '#2365AB', soft: 'rgba(35,101,171,.12)', badgeBg: 'linear-gradient(145deg,#eff6ff,#dbeafe)', badgeColor: '#1d4ed8', badgeBorder: 'rgba(37,99,235,.28)' }
                  : { accent: '#2365AB', soft: 'rgba(35,101,171,.12)', badgeBg: 'linear-gradient(145deg,#eff6ff,#dbeafe)', badgeColor: '#1d4ed8', badgeBorder: 'rgba(37,99,235,.28)' };
              const adminName = resolvePedidoAdministrativoNombre(grp.pedido);
              // Emisión administrativa genérica (sin persona asignada): visible para todo el equipo.
              const adminEmisorLabel = adminName || 'Equipo administrativo';
              const showAdminEmisor = Boolean(
                grp.pedido
                && isPedidoEmisionAdministrativo(grp.pedido)
              );
              const medName = grp.pedido ? (grp.pedido._medico_nombre || grp.pedido.medico || '') : '';
              const dosificacionPlan = grp.pedido ? resolvePlanDosificacionPedido(grp.pedido) : null;
              const emisionStatusProps = {
                emittedCount,
                totalPendingCount,
                hiddenPendientesInGroup
              };
              return (
                <div
                  key={`grp-${idx}`}
                  className="crm-icard crm-re-group-card"
                  style={{
                    '--crm-re-accent': groupTheme.accent,
                    position: 'relative',
                    zIndex: openMedicationDetails === `grp-med-${groupPid}` ? 120 : 1
                  }}
                >
                  <div
                    className="crm-icard-h"
                    style={{
                      display: 'grid',
                      gridTemplateColumns: isMobile ? '1fr' : ((isTablet || isSmallLandscape) ? '1fr auto' : '1fr auto auto'),
                      alignItems: isMobile ? 'stretch' : 'center',
                      gap: 10,
                      padding: '10px 12px',
                      overflow: 'visible'
                    }}
                  >
                    <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                        <div className="crm-re-group-count">
                          {String(totalRecetasInGroup || (grp.recetas || []).length).padStart(2, '0')}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', minWidth: 0, flex: 1 }}>
                          <div className="crm-re-patient-name" style={{ whiteSpace: isMobile ? 'normal' : 'nowrap' }}>
                            {formatPatientHeader(grp.pedido ? (grp.pedido.paciente_nombre || grp.pedido.paciente || grp.recetas[0].paciente) : grp.recetas[0].paciente, grp.pedido ? (grp.pedido._dni_display || grp.pedido.paciente_dni || grp.pedido.solicitante_dni) : '')}
                          </div>
                          <PacienteExternoBadge show={grp.pedido?.es_externo} compact />
                          <PamiBadge pedido={grp.pedido || (grp.recetas && grp.recetas[0])} compact />
                          {grp.pedido?.ingreso_origen ? (
                            <span style={{ display: 'inline-flex' }}>
                              <PedidoIngresoOrigenBadge origen={grp.pedido.ingreso_origen} conversationId={grp.pedido.ingreso_conversation_id} compact />
                            </span>
                          ) : null}
                          {isFamilyGroup ? (
                            <button
                              type="button"
                              onClick={() => navigate(`/asociacion-clientes?dni=${encodeURIComponent(titularDni)}&tab=vinculos`)}
                              title="Ver titular en Asociación clientes"
                              style={{
                                border: '1px solid #d2e2f4',
                                background: '#f8fbff',
                                color: '#1c4f86',
                                borderRadius: 999,
                                padding: '3px 8px',
                                fontSize: 10,
                                fontWeight: 700,
                                cursor: 'pointer',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              Ver titular
                            </button>
                          ) : null}
                        </div>
                      </div>
                      <div style={{ marginTop: 6, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <div className="crm-re-med-box" style={{ minWidth: isMobile ? '100%' : 210 }}>
                          {renderMedicationSummary(grp.pedido || grp.recetas[0], `grp-med-${groupPid}`, { openAbove: openMedDetailAbove })}
                        </div>
                        <span className="crm-re-chip">{grp.recetas.length} recetas en el tratamiento</span>
                        {dosificacionPlan?.hasPlan ? (
                          <TratamientoPlanResumen pedido={grp.pedido} variant="compact" />
                        ) : grp.pedido && (grp.pedido.posologia || grp.pedido._posologia) ? (
                          <span className="crm-re-chip crm-re-chip--posologia">
                            Posología: <strong>{grp.pedido.posologia || grp.pedido._posologia}</strong>
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <div
                      className={`crm-re-actors-strip${showAdminEmisor ? '' : ' crm-re-actors-strip--medico-solo'}`}
                      style={{ justifySelf: isMobile ? 'stretch' : 'end' }}
                    >
                      {!showAdminEmisor ? (
                        <div className="crm-re-emisor-panel crm-re-emisor-panel--medico">
                          {medName ? (
                            <div
                              className="crm-re-actor-card crm-re-actor-card--medico crm-re-actor-card--emisor"
                              title="Emisión a cargo del médico vinculado"
                            >
                              <div className="crm-re-actor-card__head">
                                <span className="crm-re-actor-card__icon" aria-hidden="true">⚕️</span>
                                <span className="crm-re-actor-card__label">Emisor médico</span>
                              </div>
                              <div className="crm-re-actor-card__name">{formatMedicoTitulo(medName)}</div>
                            </div>
                          ) : null}
                          <div className="crm-re-emisor-panel__divider" aria-hidden="true" />
                          <div className="crm-re-emisor-panel__status">
                            {renderRecetaEmisionStatus({ ...emisionStatusProps, layout: 'cluster' })}
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="crm-re-actors-strip__cards">
                            <div
                              className="crm-re-actor-card crm-re-actor-card--admin"
                              title={adminName
                                ? `Emisión a cargo del administrativo ${adminName}`
                                : 'Emisión administrativa: disponible para todo el equipo administrativo'}
                            >
                              <div className="crm-re-actor-card__head">
                                <span className="crm-re-actor-card__icon" aria-hidden="true">👥</span>
                                <span className="crm-re-actor-card__label-row">
                                  <span className="crm-re-actor-card__label">Administrativo emisor</span>
                                  {adminName ? <AdminEmisorPin adminName={adminName} /> : null}
                                </span>
                              </div>
                              <div className="crm-re-actor-card__name">{adminEmisorLabel}</div>
                            </div>
                            {medName ? (
                              <div
                                className="crm-re-actor-card crm-re-actor-card--medico"
                                title="Médico tratante"
                              >
                                <div className="crm-re-actor-card__head">
                                  <span className="crm-re-actor-card__icon" aria-hidden="true">⚕️</span>
                                  <span className="crm-re-actor-card__label">Médico tratante</span>
                                </div>
                                <div className="crm-re-actor-card__name">{formatMedicoTitulo(medName)}</div>
                              </div>
                            ) : null}
                          </div>
                          {renderRecetaEmisionStatus(emisionStatusProps)}
                        </>
                      )}
                    </div>
                    <button
                      type="button"
                      className={`crm-gd-chevron${openGroups.includes(idx) ? ' crm-gd-chevron--expanded' : ''}`}
                      aria-label={openGroups.includes(idx) ? 'Cerrar' : 'Abrir'}
                      onClick={() => toggleGroup(idx)}
                      style={{
                        cursor: 'pointer',
                        padding: 0,
                        marginLeft: 0,
                        justifySelf: isMobile ? 'end' : 'center',
                        alignSelf: isMobile ? 'end' : 'center'
                      }}
                    >
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{
                          display: 'block',
                          color: 'var(--hs-text)',
                          transform: openGroups.includes(idx) ? 'rotate(90deg) translateX(1px)' : 'rotate(0deg)',
                          transition: 'transform .32s cubic-bezier(.2,.9,.2,1), opacity .18s ease',
                          opacity: 0.95
                        }}
                        aria-hidden
                      >
                        <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z" fill="currentColor" />
                      </svg>
                    </button>
                  </div>
                  <div
                    ref={el => contentRefs.current[idx] = el}
                    style={{ overflow: 'hidden', transition: 'max-height .38s cubic-bezier(.2,.9,.2,1), opacity .28s ease', maxHeight: openGroups.includes(idx) ? '5000px' : '0px', opacity: openGroups.includes(idx) ? 1 : 0 }}
                  >
                    <div className="crm-re-group-body-inner" style={{ display: 'grid', gridTemplateColumns: '1fr' }}>
                      {isMobile ? (
                        <div style={{ display: 'grid', gap: 8, padding: 10 }}>
                          {grp.recetas.map((r, rowIdx) => (
                            <div key={r.id} id={`row-receta-${r.id}`} style={{ background: '#fff', border: '1px solid rgba(148,163,184,.2)', borderRadius: 10, padding: '10px 10px' }}>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                                <div style={{ fontSize: 12, color: '#475569', fontWeight: 700 }}>#{' '}Receta {r.numero}</div>
                                <div style={{ fontSize: 12, color: '#475569', textAlign: 'right', fontFamily: 'monospace' }}>{r.nro_externo || '—'}</div>
                                <div style={{ fontSize: 11, color: '#64748b' }}>Fecha prevista</div>
                                <div style={{ fontSize: 12, color: '#0f172a', textAlign: 'right', fontWeight: 700 }}>{formatFechaCorta(r.fecha_prevista)}</div>
                                <div style={{ fontSize: 11, color: '#64748b' }}>Estado</div>
                                <div style={{ textAlign: 'right' }}>{estadoBadge(r)}</div>
                              </div>
                              {grp.pedido ? (
                                <div style={{ marginTop: 6 }}>
                                  <TratamientoPlanResumen
                                    pedido={grp.pedido}
                                    receta={r}
                                    recetaNumero={r.numero}
                                    variant="receta"
                                  />
                                </div>
                              ) : null}
                              <RenovacionPacienteRespuestaCard receta={r} />
                              <div style={{ marginTop: 10, display: 'grid', gap: 8 }}>
                                {['emitida_sistema_externo', 'confirmada_dispensa'].includes(r.estado) ? (
                                  <button
                                    className="crm-sb-btn"
                                    title="Ver seguimiento"
                                    onClick={(e) => { e.stopPropagation(); const resolvedPfId = r.pedido_farmacia_id || r.pedido_id || Number(String(r.id).split('-')[0]); setTrackerPedidoId(resolvedPfId); setTrackerOpen(true); }}
                                    style={{ width: '100%', display: 'inline-flex', justifyContent: 'center', padding: '8px 10px', borderRadius: 999, cursor: 'pointer', background: '#fff', color: '#1c4f86', border: '1px solid #d2e2f4', fontWeight: 700, fontSize: '12px' }}
                                  >
                                    Ver seguimiento
                                  </button>
                                ) : null}
                                {r.estado === 'pendiente' ? (
                                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                                    <div className="crm-re-emit-actions">
                                      <button
                                        className="crm-sb-btn crm-sb-btn-warning"
                                        style={{
                                          flex: '1 1 auto',
                                          display: 'inline-flex',
                                          justifyContent: 'center',
                                          cursor: (isFechaPrevistaHabilitada(r.fecha_prevista) && !resolveSecuencialEmisionBlock(r)) ? 'pointer' : 'not-allowed',
                                          color: '#ffffff',
                                          fontSize: '12px',
                                          fontWeight: 700,
                                          opacity: (isFechaPrevistaHabilitada(r.fecha_prevista) && !resolveSecuencialEmisionBlock(r)) ? 1 : 0.55,
                                          borderRadius: 999,
                                          border: '1px solid #2365AB',
                                          background: '#2365AB'
                                        }}
                                        onClick={() => abrirEmitir(r.id)}
                                        disabled={!isFechaPrevistaHabilitada(r.fecha_prevista) || Boolean(resolveSecuencialEmisionBlock(r))}
                                        title={
                                          resolveSecuencialEmisionBlock(r)
                                            ? resolveSecuencialEmisionBlock(r)
                                            : (isFechaPrevistaHabilitada(r.fecha_prevista)
                                              ? 'Registrar emisión'
                                              : `Solo se puede registrar entre ${EMISION_WINDOW_PASADO_DIAS} días atrás y ${EMISION_WINDOW_FUTURO_DIAS} días hacia adelante`)
                                        }
                                      >
                                        Registrar emisión
                                      </button>
                                      <RecetaEmisionVentanaPin
                                        fechaPrevista={r.fecha_prevista}
                                        diasPasado={EMISION_WINDOW_PASADO_DIAS}
                                        diasFuturo={EMISION_WINDOW_FUTURO_DIAS}
                                        align="right"
                                      />
                                    </div>
                                    {hiddenPendientesInGroup > 0 && rowIdx === toggleButtonRowIdx ? (
                                      <>
                                        {isRecetaRecordatorioHabilitado(r) ? (
                                          <button
                                            type="button"
                                            className="crm-sb-btn"
                                            onClick={() => enviarMensajeVencimiento(r, grp.pedido)}
                                            disabled={sendingMensajeRecetaId === r.id}
                                            style={{
                                              display: 'inline-flex',
                                              justifyContent: 'center',
                                              borderRadius: 8,
                                              cursor: sendingMensajeRecetaId === r.id ? 'not-allowed' : 'pointer',
                                              background: '#ecfdf3',
                                              color: '#166534',
                                              border: '1px solid #a7f3d0',
                                              fontWeight: 700,
                                              fontSize: '12px',
                                              width: 'fit-content',
                                              minWidth: 0,
                                              padding: '6px 9px',
                                              opacity: sendingMensajeRecetaId === r.id ? 0.6 : 1
                                            }}
                                            title="Enviar WhatsApp al paciente"
                                          >
                                            {sendingMensajeRecetaId === r.id ? '…' : '🟢 WhatsApp'}
                                          </button>
                                        ) : null}
                                        <button
                                          type="button"
                                          className="crm-sb-btn"
                                          onClick={() => {
                                            if (!groupPid) return;
                                            setExpandedHiddenPedidos((prev) => {
                                              const next = new Set(prev);
                                              if (next.has(groupPid)) next.delete(groupPid);
                                              else next.add(groupPid);
                                              return next;
                                            });
                                          }}
                                          style={{
                                            display: 'inline-flex',
                                            justifyContent: 'center',
                                            borderRadius: 8,
                                            cursor: 'pointer',
                                            background: groupExpanded ? '#1e40af' : '#334155',
                                            color: '#fff',
                                            border: 'none',
                                            fontWeight: 700,
                                            fontSize: '12px',
                                            width: 'fit-content',
                                            minWidth: 0,
                                            padding: '6px 9px'
                                          }}
                                          title={groupExpanded ? 'Ocultar fuera de ventana' : 'Mostrar fuera de ventana'}
                                          aria-label={groupExpanded ? 'Ocultar fuera de ventana' : 'Mostrar fuera de ventana'}
                                        >
                                          {groupExpanded ? `🙈 ${hiddenPendientesInGroup}` : `👁️ ${hiddenPendientesInGroup}`}
                                        </button>
                                      </>
                                    ) : null}
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="recetas-table-wrap crm-re-recetas-table-wrap" style={{ WebkitOverflowScrolling: 'touch' }}>
                          <table className="recetas-table" style={{ width: '100%', marginTop: 0 }}>
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Fecha prevista</th>
                                <th>Cobertura</th>
                                <th>Estado</th>
                                <th>N° externo</th>
                                <th>Acción</th>
                              </tr>
                            </thead>
                            <tbody>
                              {grp.recetas.map((r, rowIdx) => (
                                <tr key={r.id} id={`row-receta-${r.id}`}>
                                  <td style={{ color: 'var(--hs-text)', fontWeight: 600 }}>Receta {r.numero}</td>
                                  <td>{formatFechaCorta(r.fecha_prevista)}</td>
                                  <td className="crm-re-table-cobertura">
                                    {grp.pedido ? (
                                      <TratamientoPlanResumen
                                        pedido={grp.pedido}
                                        receta={r}
                                        recetaNumero={r.numero}
                                        variant="receta"
                                      />
                                    ) : (
                                      <span className="crm-re-plan__muted">—</span>
                                    )}
                                  </td>
                                  <td>
                                    <div style={{ display: 'grid', gap: 6, minWidth: 180 }}>
                                      {estadoBadge(r)}
                                      <RenovacionPacienteRespuestaCard receta={r} />
                                    </div>
                                  </td>
                                  <td style={{ fontFamily: 'monospace' }}>{r.nro_externo || '—'}</td>
                                  <td style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                    {['emitida_sistema_externo', 'confirmada_dispensa'].includes(r.estado) ? (
                                      <button
                                        className="crm-sb-btn"
                                        title="Ver seguimiento"
                                        onClick={(e) => { e.stopPropagation(); const resolvedPfId = r.pedido_farmacia_id || r.pedido_id || Number(String(r.id).split('-')[0]); setTrackerPedidoId(resolvedPfId); setTrackerOpen(true); }}
                                        style={{ minWidth: 140, display: 'inline-flex', justifyContent: 'center', padding: '6px 10px', borderRadius: 8, cursor: 'pointer', background: '#2365ab', color: '#fff', border: 'none', fontWeight: 700, fontSize: '12px' }}
                                        style={{ minWidth: 136, display: 'inline-flex', justifyContent: 'center', padding: '6px 10px', borderRadius: 999, cursor: 'pointer', background: '#fff', color: '#1c4f86', border: '1px solid #d2e2f4', fontWeight: 700, fontSize: '12px' }}
                                      >
                                        Ver seguimiento
                                      </button>
                                    ) : null}
                                    {r.estado === 'pendiente' ? (
                                      <div className="crm-re-emit-actions crm-re-emit-actions--table">
                                        <button
                                          className="crm-sb-btn crm-sb-btn-warning"
                                          style={{
                                            minWidth: 136,
                                            display: 'inline-flex',
                                            justifyContent: 'center',
                                            cursor: (isFechaPrevistaHabilitada(r.fecha_prevista) && !resolveSecuencialEmisionBlock(r)) ? 'pointer' : 'not-allowed',
                                            color: '#ffffff',
                                            fontSize: '12px',
                                            fontWeight: 700,
                                            opacity: (isFechaPrevistaHabilitada(r.fecha_prevista) && !resolveSecuencialEmisionBlock(r)) ? 1 : 0.55,
                                            borderRadius: 999,
                                            border: '1px solid #2365AB',
                                            background: '#2365AB'
                                          }}
                                          onClick={() => abrirEmitir(r.id)}
                                          disabled={!isFechaPrevistaHabilitada(r.fecha_prevista) || Boolean(resolveSecuencialEmisionBlock(r))}
                                          title={
                                            resolveSecuencialEmisionBlock(r)
                                              ? resolveSecuencialEmisionBlock(r)
                                              : (isFechaPrevistaHabilitada(r.fecha_prevista)
                                                ? 'Registrar emisión'
                                                : `Solo se puede registrar entre ${EMISION_WINDOW_PASADO_DIAS} días atrás y ${EMISION_WINDOW_FUTURO_DIAS} días hacia adelante`)
                                          }
                                        >
                                          Registrar emisión
                                        </button>
                                        <RecetaEmisionVentanaPin
                                          fechaPrevista={r.fecha_prevista}
                                          diasPasado={EMISION_WINDOW_PASADO_DIAS}
                                          diasFuturo={EMISION_WINDOW_FUTURO_DIAS}
                                          align="left"
                                        />
                                      </div>
                                    ) : null}
                                    {hiddenPendientesInGroup > 0 && rowIdx === toggleButtonRowIdx ? (
                                      <>
                                        {isRecetaRecordatorioHabilitado(r) ? (
                                          <button
                                            type="button"
                                            className="crm-sb-btn"
                                            onClick={() => enviarMensajeVencimiento(r, grp.pedido)}
                                            disabled={sendingMensajeRecetaId === r.id}
                                            style={{
                                              minWidth: 126,
                                              display: 'inline-flex',
                                              justifyContent: 'center',
                                              borderRadius: 8,
                                              cursor: sendingMensajeRecetaId === r.id ? 'not-allowed' : 'pointer',
                                              background: '#ecfdf3',
                                              color: '#166534',
                                              border: '1px solid #a7f3d0',
                                              fontWeight: 700,
                                              fontSize: '12px',
                                              opacity: sendingMensajeRecetaId === r.id ? 0.6 : 1
                                            }}
                                            title="Enviar WhatsApp al paciente"
                                          >
                                            {sendingMensajeRecetaId === r.id ? '…' : '🟢 WhatsApp'}
                                          </button>
                                        ) : null}
                                        <button
                                          type="button"
                                          className="crm-sb-btn"
                                          onClick={() => {
                                            if (!groupPid) return;
                                            setExpandedHiddenPedidos((prev) => {
                                              const next = new Set(prev);
                                              if (next.has(groupPid)) next.delete(groupPid);
                                              else next.add(groupPid);
                                              return next;
                                            });
                                          }}
                                          style={{
                                            display: 'inline-flex',
                                            justifyContent: 'center',
                                            marginLeft: 'auto',
                                            borderRadius: 8,
                                            cursor: 'pointer',
                                            background: groupExpanded ? '#eef4fb' : '#fff',
                                            color: groupExpanded ? '#1c4f86' : '#33475B',
                                            border: '1px solid #d4deeb',
                                            fontWeight: 700,
                                            fontSize: '12px',
                                            width: 'fit-content',
                                            minWidth: 0,
                                            padding: '6px 9px'
                                          }}
                                          title={groupExpanded ? 'Ocultar fuera de ventana' : 'Mostrar fuera de ventana'}
                                          aria-label={groupExpanded ? 'Ocultar fuera de ventana' : 'Mostrar fuera de ventana'}
                                        >
                                          {groupExpanded ? `🙈 ${hiddenPendientesInGroup}` : `👁️ ${hiddenPendientesInGroup}`}
                                        </button>
                                      </>
                                    ) : null}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            </div>
          )}
        </div>

        {!initialLoading && groupedRecetas.length > 0 ? (
          <div className="crm-re-pagination crm-re-pagination--footer" style={{ justifyContent: isMobile ? 'center' : 'flex-end' }}>
            <div className="crm-re-pagination__inner" style={{ justifyContent: isMobile ? 'center' : 'flex-end', width: isMobile ? '100%' : 'auto' }}>
              <button type="button" className="crm-re-pagination__btn" disabled={page === 1} onClick={() => setPage(1)}>⏮</button>
              <button type="button" className="crm-re-pagination__btn" disabled={page === 1} onClick={() => setPage(p => Math.max(1, p - 1))}>◀</button>
              <span className="crm-re-pagination__label">{`Pag ${page} de ${totalPages}`}</span>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <input
                  className="crm-re-pagination__input"
                  value={pageInput}
                  onChange={handlePageInputChange}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); goToInputPage(); } }}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder={`1–${totalPages}`}
                  aria-label="Ir a página"
                />
                <button type="button" className="crm-re-pagination__go" onClick={goToInputPage}>Ir</button>
              </div>
              <button type="button" className="crm-re-pagination__btn" disabled={page === totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>▶</button>
              <button type="button" className="crm-re-pagination__btn" disabled={page === totalPages} onClick={() => setPage(totalPages)}>⏭</button>
            </div>
          </div>
        ) : null}
      </div>

      <div
        onClick={(e) => { if (e.target === e.currentTarget) closeTrackerDrawer(); }}
        style={{ position: 'fixed', inset: 0, background: trackerOpen ? 'rgba(15,23,42,0.32)' : 'rgba(15,23,42,0)', backdropFilter: trackerOpen ? 'blur(2px)' : 'none', zIndex: 240, pointerEvents: trackerOpen ? 'all' : 'none', transition: 'background .26s ease, backdrop-filter .26s ease', display: 'flex', justifyContent: 'flex-start' }}
      >
        <aside style={{ ...styles.drawer, transform: trackerOpen ? 'translateX(0)' : 'translateX(-105%)' }} aria-hidden={!trackerOpen}>
          <div style={styles.drawerHead}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#253342' }}>Seguimiento de pedido</div>
              <div style={{ fontSize: 11, color: '#7C98B6', marginTop: 2 }}>Últimos movimientos en tiempo real</div>
            </div>
            <button
              type="button"
              style={styles.drawerClose}
              onClick={closeTrackerDrawer}
              aria-label="Cerrar seguimiento"
            >
              ✕
            </button>
          </div>
          <div style={styles.drawerBody}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 9px', borderRadius: 999, background: '#f4f8ff', border: '1px solid #dbe8f8', fontSize: 11, fontWeight: 700, color: '#1c4f86', width: 'fit-content' }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#34d399', animation: 'trackerPulse 1.6s ease-in-out infinite' }} />
              Live
            </div>
            <div style={{ marginBottom: 4, padding: '10px 12px', borderRadius: 12, background: '#fff', border: '1px solid #dfe7f2', boxShadow: '0 1px 2px rgba(15,23,42,.04)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <div style={{ color: '#475569', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em' }}>Estado actual</div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '5px 10px', borderRadius: 999, background: '#f8fbff', border: '1px solid #d7e5f8' }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#1d4ed8' }} />
                <strong style={{ color: '#1e3a8a', fontSize: 13 }}>{trackerEstado ? (typeof estadoLabel === 'function' ? estadoLabel(trackerEstado) : trackerEstado) : 'Desconocido'}</strong>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingRight: 2, position: 'relative' }}>
              <div style={{ position: 'absolute', left: 24, top: 6, bottom: 6, width: 2, background: 'linear-gradient(180deg,#dbe8f8 0%, #e8eef6 100%)', borderRadius: 999 }} />
              {TRACK_STATES.map((st, i) => {
                // show 'pendiente_call_center' only when current estado is unknown; otherwise hide per previous request
                if (st === 'pendiente_call_center') {
                  if (trackerEstado) return null;
                }
                // hide 'cancelado' when pedido is entregado
                if (st === 'cancelado' && trackerEstado === 'entregado') return null;
                const isActive = trackerEstado === st;
                // Do not mark prior states as "Completado" when the current estado is 'cancelado'
                let passed = false;
                if (trackerEstado && trackerEstado !== 'cancelado') {
                  passed = TRACK_STATES.indexOf(st) < TRACK_STATES.indexOf(trackerEstado);
                }
                const histRow = trackerHistory && trackerHistory.find(h => String(h.new_estado) === String(st));
                let statusText = '';
                if (isActive) {
                  if (st === 'entregado') statusText = 'Pedido completado';
                  else if (st === 'cancelado') statusText = 'Cancelado';
                  else statusText = 'Vigente';
                } else {
                  statusText = histRow ? formatDateTs(histRow.created_at) : (passed ? 'Completado' : '');
                }
                const stepTone = isActive ? '#2563eb' : (passed ? '#0f766e' : '#94a3b8');
                const stepBg = isActive ? 'linear-gradient(180deg,rgba(37,99,235,.1),rgba(37,99,235,.04))' : (passed ? 'linear-gradient(180deg,rgba(16,185,129,.1),rgba(16,185,129,.03))' : 'linear-gradient(180deg,#fff,#fafcff)');
                const stepBorder = isActive ? 'rgba(37,99,235,.34)' : (passed ? 'rgba(16,185,129,.32)' : 'rgba(148,163,184,.26)');

                return (
                  <div key={st} style={{ display: 'grid', gridTemplateColumns: isMobile ? '36px 1fr' : '42px 1fr auto', gap: 10, alignItems: 'center', padding: '9px 10px', borderRadius: 12, background: stepBg, border: `1px solid ${stepBorder}`, position: 'relative' }}>
                    <div style={{ width: 30, height: 30, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: stepTone, background: '#fff', border: `1px solid ${stepBorder}`, boxShadow: isActive ? '0 0 0 4px rgba(37,99,235,.12), 0 3px 8px rgba(15,23,42,.08)' : '0 2px 6px rgba(15,23,42,.07)', animation: isActive ? 'trackerFloat 2s ease-in-out infinite' : 'none' }}>
                      {trackerStateIcon(st)}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <div style={{ color: isActive ? '#0f172a' : '#334155', fontWeight: isActive ? 800 : 700, fontSize: 13 }}>{labelizeState(st)}</div>
                      <div style={{ color: stepTone, fontSize: 11, fontWeight: 700 }}>{statusText || '—'}</div>
                    </div>
                    <div style={{ minWidth: isMobile ? 0 : 130, textAlign: 'right', color: '#64748b', fontSize: 11, gridColumn: isMobile ? '2 / 3' : 'auto' }}>
                      {histRow && histRow.changed_by ? <div>Por: <strong style={{ color: '#0f172a', fontWeight: 700 }}>{histRow.changed_by}</strong></div> : <div>&nbsp;</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div style={styles.drawerFoot}>
            <button
              className="crm-btn crm-btn-outline"
              style={{ width: '100%', borderRadius: 999, fontWeight: 700, borderColor: '#dce6f2', background: '#fff', justifyContent: 'center', minHeight: 40 }}
              onClick={closeTrackerDrawer}
            >
              Cerrar
            </button>
          </div>
        </aside>
      </div>
      <div
        onClick={(e) => { if (e.target === e.currentTarget) setModalHistorialGlobalOpen(false); }}
        style={{ position: 'fixed', inset: 0, background: modalHistorialGlobalOpen ? 'rgba(15,23,42,.26)' : 'rgba(15,23,42,0)', zIndex: 260, pointerEvents: modalHistorialGlobalOpen ? 'all' : 'none', transition: 'background .28s ease', display: 'flex', justifyContent: 'flex-end' }}
      >
        <aside
          className="crm-re-historial-drawer"
          style={{
            width: isMobile ? '100vw' : undefined,
            minWidth: isMobile ? 0 : undefined,
            transform: modalHistorialGlobalOpen ? 'translateX(0)' : 'translateX(105%)'
          }}
        >
          <div className="crm-gd-drawer-head" style={{ marginBottom: 12, paddingBottom: 12 }}>
            <div>
              <div className="crm-gd-drawer-head__title">Historial global de tratamientos</div>
              <div className="crm-gd-drawer-head__sub">Consultá historial aunque el pedido ya no esté en pendientes</div>
            </div>
            <button type="button" className="crm-gd-btn" onClick={() => setModalHistorialGlobalOpen(false)}>Cerrar</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(220px,1.1fr) minmax(220px,1fr)', gap: 10, alignItems: 'end', marginBottom: 10 }}>
            <div>
              <label style={{ fontSize: 12, color: 'var(--hs-text-xlight)', marginBottom: 6, display: 'block' }}>Paciente (DNI)</label>
              <input value={historialGlobalPacienteSearch} onChange={(e) => setHistorialGlobalPacienteSearch(e.target.value)} placeholder="Buscar paciente en el selector..." style={styles.formInput} />
            </div>
            <div>
              <select value={historialGlobalDni} onChange={(e) => setHistorialGlobalDni(e.target.value)} style={{ ...styles.formInput, cursor: 'pointer' }}>
                <option value="">Seleccioná un paciente</option>
                {filteredHistorialPacientes.map((p) => (
                  <option key={`hgp-${p.dni}`} value={p.dni}>{`${p.nombre} · DNI ${String(p.dni).replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`}</option>
                ))}
              </select>
            </div>
          </div>
          <div style={{ marginBottom: 8, fontSize: 12, color: '#475569', fontWeight: 700 }}>{filteredHistorialGlobalItems.length} registros</div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.5fr repeat(5, minmax(0,1fr))', gap: 8, marginBottom: 10 }}>
            <div>
              <label style={{ fontSize: 12, color: 'var(--hs-text-xlight)', marginBottom: 6, display: 'block' }}>Buscar en historial global</label>
              <input value={historialGlobalSearch} onChange={(e) => setHistorialGlobalSearch(e.target.value)} placeholder="Medicamento, estado, medico, nro externo..." disabled={!historialGlobalDni} style={{ ...styles.formInput, opacity: historialGlobalDni ? 1 : 0.6, cursor: historialGlobalDni ? 'text' : 'not-allowed' }} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: 'var(--hs-text-xlight)', marginBottom: 6, display: 'block' }}>Estado</label>
              <select value={historialGlobalEstado} onChange={(e) => setHistorialGlobalEstado(e.target.value)} disabled={!historialGlobalDni} style={{ ...styles.formInput, cursor: historialGlobalDni ? 'pointer' : 'not-allowed', opacity: historialGlobalDni ? 1 : 0.6 }}>
                <option value="todos">Todos</option>
                <option value="pendiente">Pendiente</option>
                <option value="emitida_sistema_externo">Emitida</option>
                <option value="confirmada_dispensa">Con dispensa</option>
                <option value="entregada">Entregada</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, color: 'var(--hs-text-xlight)', marginBottom: 6, display: 'block' }}>Desde</label>
              <input type="date" value={historialGlobalDesde} onChange={(e) => setHistorialGlobalDesde(e.target.value)} disabled={!historialGlobalDni} style={{ ...styles.formInput, opacity: historialGlobalDni ? 1 : 0.6, cursor: historialGlobalDni ? 'text' : 'not-allowed' }} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: 'var(--hs-text-xlight)', marginBottom: 6, display: 'block' }}>Hasta</label>
              <input type="date" value={historialGlobalHasta} onChange={(e) => setHistorialGlobalHasta(e.target.value)} disabled={!historialGlobalDni} style={{ ...styles.formInput, opacity: historialGlobalDni ? 1 : 0.6, cursor: historialGlobalDni ? 'text' : 'not-allowed' }} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: 'var(--hs-text-xlight)', marginBottom: 6, display: 'block' }}>Orden</label>
              <select value={historialGlobalOrder} onChange={(e) => setHistorialGlobalOrder(e.target.value)} disabled={!historialGlobalDni} style={{ ...styles.formInput, cursor: historialGlobalDni ? 'pointer' : 'not-allowed', opacity: historialGlobalDni ? 1 : 0.6 }}>
                <option value="fecha_desc">Mas reciente</option>
                <option value="fecha_asc">Mas antiguo</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, color: 'var(--hs-text-xlight)', marginBottom: 6, display: 'block' }}>Mostrar</label>
              <select value={historialGlobalItemsPerPage} onChange={(e) => setHistorialGlobalItemsPerPage(Number(e.target.value))} disabled={!historialGlobalDni} style={{ ...styles.formInput, cursor: historialGlobalDni ? 'pointer' : 'not-allowed', minWidth: 90, opacity: historialGlobalDni ? 1 : 0.6 }}>
                <option value={6}>6</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
              </select>
            </div>
          </div>
          {!historialGlobalDni ? (
            <div style={{ marginTop: 16, border: '1px dashed rgba(148,163,184,.35)', borderRadius: 12, padding: 24, textAlign: 'center', color: '#64748b' }}>
              <div style={{ fontSize: 26, marginBottom: 6 }}>🗂️</div>
              <div style={{ fontWeight: 700 }}>Sin historial para mostrar</div>
              <div style={{ marginTop: 4, fontSize: 13 }}>Seleccioná otro paciente o ajustá el buscador para encontrar tratamientos aprobados o rechazados.</div>
            </div>
          ) : pageHistorialGlobalItems.length === 0 ? (
            <div style={{ marginTop: 16, border: '1px dashed rgba(148,163,184,.35)', borderRadius: 12, padding: 24, textAlign: 'center', color: '#64748b' }}>
              <div style={{ fontSize: 26, marginBottom: 6 }}>🗂️</div>
              <div style={{ fontWeight: 700 }}>Sin historial para mostrar</div>
              <div style={{ marginTop: 4, fontSize: 13 }}>No se encontraron registros con esos filtros.</div>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 8 }}>
              {pageHistorialGlobalItems.map((h) => (
                <div key={`hgi-${h.id}`} style={{ marginTop: 2, padding: '10px 10px', display: 'flex', gap: 10, borderRadius: 12, border: '1px solid rgba(148,163,184,.2)', background: 'linear-gradient(180deg,#ffffff,#f8fafc)' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                      <div style={{ minWidth: 0, flex: 1 }}>{renderMedicationSummary(h, `hist-med-${h.id}`)}</div>
                      <div style={{ marginTop: 3, fontSize: 12, color: '#64748b' }}>Receta {h.numero || '—'} {h.nro_externo ? `· N° ${h.nro_externo}` : ''}</div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4, flexWrap: 'wrap' }}>
                        {String(h.estado) === 'emitida_sistema_externo' && (h.pedido_farmacia_id || h.pedido_id) ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              const resolvedPfId = h.pedido_farmacia_id || h.pedido_id;
                              if (!resolvedPfId) return;
                              setModalHistorialGlobalOpen(false);
                              setTrackerReturnToHistorial(true);
                              setTrackerPedidoId(resolvedPfId);
                              setTrackerOpen(true);
                            }}
                            style={{ border: '1px solid rgba(37,99,235,.24)', background: 'linear-gradient(180deg,#ffffff,#f8fbff)', color: '#1d4ed8', borderRadius: 999, padding: '5px 9px', fontSize: 11, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', lineHeight: 1.1, minHeight: 26, display: 'inline-flex', alignItems: 'center' }}
                          >
                            Abrir trazabilidad
                          </button>
                        ) : null}
                        <div style={{ opacity: (String(h.estado) === 'emitida_sistema_externo' && (h.pedido_farmacia_id || h.pedido_id)) ? 1 : 0.75 }}>
                          {estadoBadge(h)}
                        </div>
                      </div>
                    </div>
                    <div style={{ marginTop: 6, fontSize: 12, color: '#33475B', fontWeight: 700 }}>{h.medico || 'Médico no informado'}</div>
                    {h.posologia ? <div style={{ marginTop: 8, fontSize: 13, color: '#516F90', background: 'rgba(37,99,235,.08)', padding: '5px 8px', borderRadius: 8 }}><strong>Posologia:</strong> {h.posologia}</div> : null}
                    {h.notas ? <div style={{ marginTop: 8, fontSize: 13, color: '#516F90' }}><strong>Notas:</strong> {h.notas}</div> : null}
                    {h.fecha_prevista ? <div style={{ marginTop: 8, fontSize: 11, color: 'var(--hs-text-xlight)', fontWeight: 700 }}>Prevista: {h.fecha_prevista}</div> : null}
                    {h.fecha_emitida ? <div style={{ marginTop: 4, fontSize: 11, color: 'var(--hs-text-xlight)', fontWeight: 700 }}>Emitida: {formatDateTs(h.fecha_emitida)}</div> : null}
                  </div>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 }}>
                <div style={{ fontSize: 12, color: '#64748b', fontWeight: 700 }}>
                  Mostrando {Math.min((historialGlobalPage - 1) * historialGlobalItemsPerPage + 1, filteredHistorialGlobalItems.length)}-{Math.min(historialGlobalPage * historialGlobalItemsPerPage, filteredHistorialGlobalItems.length)} de {filteredHistorialGlobalItems.length}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <button disabled={historialGlobalPage <= 1} onClick={() => setHistorialGlobalPage((p) => Math.max(1, p - 1))} style={{ padding: '6px 10px', borderRadius: 999, border: '1px solid rgba(148,163,184,.3)', background: historialGlobalPage <= 1 ? '#f1f3f6' : '#fff', cursor: historialGlobalPage <= 1 ? 'not-allowed' : 'pointer', fontWeight: 700 }}>◀</button>
                  <div style={{ fontSize: 12, color: '#475569', fontWeight: 700 }}>Página {historialGlobalPage} / {totalHistorialGlobalPages}</div>
                  <button disabled={historialGlobalPage >= totalHistorialGlobalPages} onClick={() => setHistorialGlobalPage((p) => Math.min(totalHistorialGlobalPages, p + 1))} style={{ padding: '6px 10px', borderRadius: 999, border: '1px solid rgba(148,163,184,.3)', background: historialGlobalPage >= totalHistorialGlobalPages ? '#f1f3f6' : '#fff', cursor: historialGlobalPage >= totalHistorialGlobalPages ? 'not-allowed' : 'pointer', fontWeight: 700 }}>▶</button>
                </div>
              </div>
            </div>
          )}
        </aside>
      </div>
      <style>
        {`
          @keyframes trackerPulse {
            0%, 100% { opacity: .55; transform: scale(1); }
            50% { opacity: 1; transform: scale(1.15); }
          }
          @keyframes trackerFloat {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-2px); }
          }
        `}
      </style>

      {modalEmitirOpen && (() => {
        const convId = selectedEmisionContext?.pedido?.ingreso_conversation_id
          || selectedEmisionContext?.pedido?.conversation_id
          || null;
        const hasConversation = Boolean(convId);
        const planCount = selectedEmisionContext?.recetasGrupo?.length ?? 1;
        return (
        <div style={styles.modalBackdrop} className="crm-rr-approve-backdrop">
          <div className="crm-rr-approve-modal crm-rr-approve-modal--emit" role="dialog" aria-modal="true" aria-labelledby="crm-re-emit-title" style={{ position: 'relative' }}>
            {confirmandoEmision ? (
              <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,.72)', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2 }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 700, color: '#334155' }}>
                  <span style={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid #cbd5e1', borderTopColor: '#2365AB', animation: 'pedidos-call-center-spin .8s linear infinite' }} />
                  Registrando emisión...
                </div>
              </div>
            ) : null}
            <header className="crm-rr-approve-hero">
              <div className="crm-rr-approve-hero__main">
                <span className="crm-rr-approve-hero__icon crm-rr-approve-hero__icon--emit" aria-hidden="true">📄</span>
                <div className="crm-rr-approve-hero__copy">
                  <h3 id="crm-re-emit-title" className="crm-rr-approve-hero__title">Registrar emisión</h3>
                  <p className="crm-rr-approve-hero__sub">
                    {selectedEmisionContext?.receta
                      ? `Receta ${selectedEmisionContext.receta.numero} · ${selectedEmisionContext.receta.paciente || 'Paciente'}`
                      : 'Ingresá el número de receta del sistema externo'}
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="crm-rr-approve-close"
                onClick={() => setModalEmitirOpen(false)}
                disabled={confirmandoEmision}
                aria-label="Cerrar"
              >
                ✕
              </button>
            </header>

            <nav className="crm-rr-approve-steps" aria-label="Pasos para registrar emisión">
              <button
                type="button"
                className={`crm-rr-approve-steps__item${emitStep === 'whatsapp' ? ' is-active' : ''}${emitStep === 'receta' ? ' is-done' : ''}`}
                onClick={() => setEmitStep('whatsapp')}
                disabled={confirmandoEmision}
                aria-current={emitStep === 'whatsapp' ? 'step' : undefined}
              >
                <span className="crm-rr-approve-steps__num">1</span>
                <span>WhatsApp</span>
              </button>
              <span className="crm-rr-approve-steps__line" aria-hidden="true" />
              <button
                type="button"
                className={`crm-rr-approve-steps__item${emitStep === 'receta' ? ' is-active' : ''}`}
                onClick={() => setEmitStep('receta')}
                disabled={confirmandoEmision}
                aria-current={emitStep === 'receta' ? 'step' : undefined}
              >
                <span className="crm-rr-approve-steps__num">2</span>
                <span>Receta emitida</span>
              </button>
            </nav>

            <div className="crm-rr-approve-body">
              {emitStep === 'whatsapp' ? (
                <section className="crm-rr-approve-step crm-rr-approve-step--wa">
                  <div className="crm-rr-approve-section-head">
                    <span className="crm-rr-approve-section-head__icon" aria-hidden="true">💬</span>
                    <div>
                      <div className="crm-rr-approve-section-head__title">WhatsApp de emisión</div>
                      <p className="crm-rr-approve-section-head__hint">
                        Prepará el aviso al paciente antes de registrar la receta como emitida.
                      </p>
                    </div>
                  </div>
                  <div className="crm-rr-approve-wa-grid">
                    <button
                      type="button"
                      className={`crm-rr-approve-wa-card crm-rr-approve-wa-card--template${!hasConversation ? ' is-disabled' : ''}`}
                      onClick={() => hasConversation && openTemplatePicker(convId)}
                      disabled={!hasConversation || confirmandoEmision}
                    >
                      <span className="crm-rr-approve-wa-card__icon" aria-hidden="true">📋</span>
                      <span className="crm-rr-approve-wa-card__copy">
                        <strong>Usar plantilla aprobada</strong>
                        <small>
                          {hasConversation
                            ? 'Elegí una plantilla de WhatsApp y enviála antes de registrar.'
                            : 'Este pedido no tiene una conversación vinculada.'}
                        </small>
                      </span>
                      <span className="crm-rr-approve-wa-card__badge">
                        {hasConversation ? 'Disponible' : 'Sin chat'}
                      </span>
                    </button>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={emitWa}
                      aria-label="Enviar WhatsApp de emisión de receta al paciente"
                      className={`crm-rr-approve-wa-card crm-rr-approve-wa-card--switch${emitWa ? ' is-on' : ''}`}
                      onClick={() => setEmitWa((v) => !v)}
                      disabled={confirmandoEmision}
                    >
                      <span className="crm-rr-approve-wa-card__icon" aria-hidden="true">📄</span>
                      <span className="crm-rr-approve-wa-card__copy">
                        <strong>WhatsApp · Emitir receta</strong>
                        <small>
                          {planCount > 1
                            ? `Aviso por WhatsApp al paciente al registrar esta emisión (${planCount} recetas en el plan).`
                            : 'Aviso por WhatsApp al paciente al registrar esta emisión.'}
                        </small>
                      </span>
                      <span className={`crm-rr-approve-switch${emitWa ? ' is-on' : ''}`} aria-hidden="true">
                        <span className="crm-rr-approve-switch__knob" />
                      </span>
                      <span className={`crm-rr-approve-wa-card__state${emitWa ? ' is-on' : ''}`}>
                        {emitWa ? 'Activo' : 'Off'}
                      </span>
                    </button>
                  </div>
                  <div className="crm-rr-approve-note">
                    <strong>Paso 1</strong>
                    <span>La plantilla es manual; el switch activa el aviso automático cuando registres la emisión.</span>
                  </div>
                </section>
              ) : (
                <section className="crm-rr-approve-step crm-rr-approve-step--plan">
                  <div className="crm-rr-approve-plan-hero">
                    <span className="crm-rr-approve-plan-hero__icon" aria-hidden="true">✓</span>
                    <div>
                      <div className="crm-rr-approve-plan-hero__title">Registrar receta emitida</div>
                      <p className="crm-rr-approve-plan-hero__text">
                        Se marcará la receta como emitida en el sistema externo y quedará visible en el circuito de trazabilidad.
                      </p>
                    </div>
                  </div>
            {selectedEmisionContext?.pedido ? (
              <TratamientoPlanResumen
                pedido={selectedEmisionContext.pedido}
                receta={selectedEmisionContext.receta}
                recetaNumero={selectedEmisionContext.receta?.numero}
                recetasEstado={selectedEmisionContext.recetasGrupo}
                variant="modal"
              />
            ) : null}
                  <div className="crm-rr-approve-fields">
                    <label className="crm-rr-approve-field">
                      <span className="crm-rr-approve-field__label">N° de receta (sistema externo)</span>
                      <input
                        className="crm-rr-approve-field__input"
                        value={emitirNro}
                        onChange={(e) => setEmitirNro(e.target.value)}
                        disabled={confirmandoEmision}
                      />
                    </label>
                    <label className="crm-rr-approve-field">
                      <span className="crm-rr-approve-field__label">URL de la receta</span>
                      <input
                        type="url"
                        className="crm-rr-approve-field__input"
                        value={emitirUrl}
                        onChange={(e) => setEmitirUrl(e.target.value)}
                        placeholder="https://..."
                        disabled={confirmandoEmision}
                      />
                    </label>
                  </div>
                  {emitirUrl.trim() && !emitirUrlValid ? (
                    <div className="crm-rr-approve-note crm-rr-approve-note--error">
                      <strong>URL inválida</strong>
                      <span>Ingresá una URL válida comenzando con http:// o https://.</span>
                    </div>
                  ) : null}
                  <div className="crm-rr-approve-plan-grid">
                    <div className="crm-rr-approve-kv">
                      <span>Receta</span>
                      <strong>{selectedEmisionContext?.receta?.numero ? `Receta ${selectedEmisionContext.receta.numero}` : '—'}</strong>
                    </div>
                    <div className="crm-rr-approve-kv">
                      <span>WhatsApp</span>
                      <strong>{emitWa ? 'Activo' : 'Off'}</strong>
                    </div>
                  </div>
                </section>
              )}
            </div>

            <footer className={`crm-rr-approve-footer${emitStep === 'receta' ? ' crm-rr-approve-footer--three' : ''}`}>
              <button
                type="button"
                className="crm-rr-approve-btn crm-rr-approve-btn--ghost"
                onClick={() => setModalEmitirOpen(false)}
                disabled={confirmandoEmision}
              >
                Cancelar
              </button>
              {emitStep === 'receta' ? (
                <button
                  type="button"
                  className="crm-rr-approve-btn crm-rr-approve-btn--ghost"
                  onClick={() => setEmitStep('whatsapp')}
                  disabled={confirmandoEmision}
                >
                  Atrás
                </button>
              ) : null}
              {emitStep === 'whatsapp' ? (
                <button
                  type="button"
                  className="crm-rr-approve-btn crm-rr-approve-btn--primary"
                  onClick={() => setEmitStep('receta')}
                  disabled={confirmandoEmision}
                >
                  Siguiente
                </button>
              ) : (
                <button
                  type="button"
                  className="crm-rr-approve-btn crm-rr-approve-btn--primary"
                  onClick={() => { void confirmarEmision(); }}
                  disabled={!canSubmitEmision}
                >
                  {confirmandoEmision ? 'Registrando…' : 'Registrar como emitida'}
                </button>
              )}
            </footer>
          </div>
        </div>
        );
      })()}

      {templateModalOpen ? (
        <div
          className="crm-cw-tpl-overlay"
          role="dialog"
          aria-label="Enviar plantilla de WhatsApp"
          onClick={(e) => {
            if (e.target === e.currentTarget && !sendingTemplate) closeTemplateModal();
          }}
        >
          <div className="crm-cw-tpl-modal">
            <div className="crm-cw-tpl-head">
              <div className="crm-cw-tpl-head__title">
                <span aria-hidden="true">📋</span>
                <strong>Plantillas de WhatsApp</strong>
              </div>
              <button
                type="button"
                className="crm-cw-tpl-close"
                onClick={closeTemplateModal}
                disabled={sendingTemplate}
                aria-label="Cerrar"
              >
                ✕
              </button>
            </div>
            <p className="crm-cw-tpl-sub">
              Elegí una plantilla aprobada para enviar al paciente.
            </p>

            {templatesLoading ? (
              <div className="crm-cw-tpl-loading">Cargando plantillas…</div>
            ) : templatesError && !selectedTemplate ? (
              <div className="crm-cw-tpl-error">{templatesError}</div>
            ) : !selectedTemplate ? (
              templates.length === 0 ? (
                <div className="crm-cw-tpl-empty">
                  No hay plantillas aprobadas en este inbox de WhatsApp.
                </div>
              ) : (
                <div className="crm-cw-tpl-list">
                  {templates
                    .filter((tpl) => tpl.name === 'saludo_inicial')
                    .map((tpl) => (
                    <button
                      key={`${tpl.name}-${tpl.language}`}
                      type="button"
                      className="crm-cw-tpl-item"
                      onClick={() => handlePickTemplate(tpl)}
                    >
                      <div className="crm-cw-tpl-item__head">
                        <strong>{tpl.name}</strong>
                        <span className="crm-cw-tpl-item__lang">{tpl.language}</span>
                        {tpl.category ? (
                          <span className="crm-cw-tpl-item__cat">{tpl.category}</span>
                        ) : null}
                      </div>
                      <span className="crm-cw-tpl-item__body">{tpl.body || '(sin cuerpo)'}</span>
                      {tpl.params_count > 0 ? (
                        <span className="crm-cw-tpl-item__params">
                          {tpl.params_count} variable{tpl.params_count > 1 ? 's' : ''} para completar
                        </span>
                      ) : null}
                    </button>
                  ))}
                </div>
              )
            ) : (
              <div className="crm-cw-tpl-detail">
                <button
                  type="button"
                  className="crm-cw-tpl-back"
                  onClick={() => {
                    setSelectedTemplate(null);
                    setTemplateParamValues([]);
                    setTemplatesError('');
                  }}
                  disabled={sendingTemplate}
                >
                  ← Volver a la lista
                </button>
                <div className="crm-cw-tpl-detail__name">
                  <strong>{selectedTemplate.name}</strong>
                  <span className="crm-cw-tpl-item__lang">{selectedTemplate.language}</span>
                </div>
                {selectedTemplate.header_text ? (
                  <div className="crm-cw-tpl-preview crm-cw-tpl-preview--header">
                    {selectedTemplate.header_text}
                  </div>
                ) : null}
                <div className="crm-cw-tpl-preview">
                  {fillTemplateBody(selectedTemplate.body, templateParamValues)}
                </div>
                {selectedTemplate.footer_text ? (
                  <div className="crm-cw-tpl-footer">{selectedTemplate.footer_text}</div>
                ) : null}
                {selectedTemplate.params_count > 0 ? (
                  <div className="crm-cw-tpl-params">
                    {Array.from({ length: selectedTemplate.params_count }).map((_, idx) => (
                      <label key={idx} className="crm-cw-tpl-param">
                        <span>Variable {`{{${idx + 1}}}`}</span>
                        <input
                          type="text"
                          value={templateParamValues[idx] || ''}
                          placeholder={`Valor para {{${idx + 1}}}`}
                          onChange={(e) => {
                            const next = [...templateParamValues];
                            next[idx] = e.target.value;
                            setTemplateParamValues(next);
                          }}
                        />
                      </label>
                    ))}
                  </div>
                ) : null}
                {templatesError ? (
                  <div className="crm-cw-tpl-error">{templatesError}</div>
                ) : null}
                <button
                  type="button"
                  className="crm-cw-tpl-send"
                  onClick={handleSendTemplate}
                  disabled={sendingTemplate}
                >
                  {sendingTemplate ? 'Enviando…' : 'Enviar plantilla'}
                </button>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </section>
  );
}
